// INDEX_SPLIT_B4_v1 (07.09.2026) -- Profil + Einstellungen: ProfilePage, DataExportCard, TeamAccountCard, SalonLogoUpload, SettingsPage
// 1:1 aus app.jsx verschoben (Reihenfolge unverändert), nur `export` ergänzt und Imports erzeugt (scripts/split_frontend.mjs).
import { AlertCircle, AlertTriangle, Bot, Check, CheckCircle, Clock, CreditCard, Download, ExternalLink, Eye, Globe, Lock, MessageSquare, Phone, PhoneForwarded, RefreshCw, Shield, Star, Users, Zap, useEffect, useRef, useState } from "../core/vendor.js";
import { API_BASE, api } from "../core/api.js";
import { COLORS, UserContext, isKosmetikSalon, today } from "../core/config.jsx";
import { Badge, Btn, Card, Input, Select, Wm } from "../core/ui.jsx";
import { OwnerPinCard } from "../core/ownerpin.jsx"; // OWNER_PIN_v1
import { activeSeason } from "../calendar/seasonal.jsx"; // SEASONAL_DECOR_HERBST_v1
import { SickDayNotifyModal } from "../employees/employees.jsx";
import { Header } from "../layout/layout.jsx";


    export const ProfilePage = ({ currentUser }) => {
  // THEME_TOGGLE_v1: state + helper
  const [theme, setTheme] = useState(typeof window !== "undefined" ? (localStorage.getItem("theme") || "dark") : "dark");
  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    try { localStorage.setItem("theme", next); } catch(e) {}
    document.documentElement.setAttribute("data-theme", next);
    setTheme(next);
  };
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [msg, setMsg] = useState("");
  const [msgType, setMsgType] = useState("success");

  const handleChangePw = () => {
    if (!oldPw || !newPw) { setMsg("Bitte alle Felder ausfüllen"); setMsgType("error"); return; }
    if (newPw.length < 8) { setMsg("Passwort muss mind. 8 Zeichen + 1 Zahl/Sonderzeichen haben"); setMsgType("error"); return; }
    if (newPw !== confirmPw) { setMsg("Passwörter stimmen nicht überein"); setMsgType("error"); return; }
    fetch("/api/profile/password", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": "Bearer " + sessionStorage.getItem("sp_token") },
      body: JSON.stringify({ old_password: oldPw, new_password: newPw })
    }).then(r => r.json()).then(d => {
      if (d.error) { setMsg(d.error); setMsgType("error"); }
      else {
        // SEC_BLOCK2_v1 (SEC-005) -- alte Anmeldung ohne Sitzung: der Server gibt neue Tokens mit, sonst wäre man gleich abgemeldet
        if (d.access_token && d.refresh_token) { try { api.setTokens(d.access_token, d.refresh_token); } catch (e) {} }
        setMsg("Passwort erfolgreich geändert!"); setMsgType("success"); setOldPw(""); setNewPw(""); setConfirmPw("");
      }
    }).catch(() => { setMsg("Fehler beim Ändern"); setMsgType("error"); });
  };

  const inputStyle = { width: "100%", padding: "10px 12px", border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 14 };

  // SHIFT_OVERRIDE_SELF_v1 (5.1) -- Mitarbeiter-Self-Krankmeldung
  const [selfOvSaving, setSelfOvSaving] = useState(false);
  const [selfOvMsg, setSelfOvMsg] = useState("");
  // SICK_DAY_NOTIFY_v1 (5.3) -- nach Self-Krankmeldung: eigene Termine durchgehen
  const [selfSickModal, setSelfSickModal] = useState(null);
  const [selfEmployees, setSelfEmployees] = useState([]);
  const _empIdSelf = currentUser && currentUser.employee_id;
  useEffect(() => {
    if (currentUser && currentUser.role === "tax_consultant") return;  // SEC_BLOCK2_v1 (SEC-002) -- Server: 403
    api.getEmployees().then(r => { if (r && r.ok) setSelfEmployees(r.data || []); }).catch(() => {});
  }, []);
  const submitSelfOverride = async (otype, extra = {}) => {
    if (!_empIdSelf) return;
    setSelfOvSaving(true);
    setSelfOvMsg("");
    // BLOCKER_SERIES_TZ_FIX_v1 -- lokales Datum (UTC waere zwischen 0 und 2 Uhr noch "gestern")
    const _d = new Date();
    const today = `${_d.getFullYear()}-${String(_d.getMonth() + 1).padStart(2, "0")}-${String(_d.getDate()).padStart(2, "0")}`;
    const body = { employee_id: Number(_empIdSelf), date: today, override_type: otype, ...extra };
    try {
      const res = await fetch("/api/shift-overrides", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": "Bearer " + sessionStorage.getItem("sp_token") },
        body: JSON.stringify(body),
      });
      const ok = res.ok;
      if (ok) {
        setSelfOvMsg(otype === "sick"
          ? "Krankmeldung gespeichert. Online-Buchung & KI-Tele blockieren dich für heute."
          : otype === "late" ? `Verspätung bis ${extra.start_time} eingetragen.`
          : "Eintrag gespeichert.");
        // SICK_DAY_NOTIFY_v1: bei sick gleich Termine durchgehen anbieten
        if (otype === "sick") {
          setSelfSickModal({ date: today, employeeId: _empIdSelf, employeeName: currentUser.name || "Du" });
        }
      } else {
        const d = await res.json().catch(() => ({}));
        setSelfOvMsg("Fehler: " + (d.error || res.status));
      }
    } catch (e) {
      setSelfOvMsg("Fehler: " + (e.message || "unbekannt"));
    }
    setSelfOvSaving(false);
  };

  return (
    <div>
      {/* HEADER_ALL_PAGES_v1 (M2): Standard-Header mit Menü-Taste -- Seite war am Handy eine Sackgasse (kein Zurück/Menü) */}
      <Header title="Mein Profil" subtitle="Persönliche Daten, Passwort & Erscheinungsbild" />

      {/* SHIFT_OVERRIDE_SELF_v1 + SELF_SICK_TOGGLE_v1 -- nur fuer Mitarbeiter UND wenn Owner es erlaubt */}
      {currentUser && currentUser.role === "employee" && _empIdSelf && currentUser.salon_allow_self_sick_leave !== false && (
        <div style={{ background: "var(--card)", borderRadius: 12, padding: 20, border: "1px solid var(--border)", marginBottom: 20 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>Heute krank oder verspätet?</h3>
          <p style={{ fontSize: 12, color: "var(--textLight)", margin: "0 0 12px" }}>
            Sofort-Wirkung: Online-Buchung & KI-Tele blockieren dich für heute. Inhaber wird im Audit-Log informiert.
          </p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button onClick={() => { if (confirm("Heute komplett krank melden?")) submitSelfOverride("sick"); }}
              disabled={selfOvSaving}
              style={{ padding: "10px 18px", background: "#DC2626", color: "white", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 14 }}>
              🤒 Heute krank melden
            </button>
            <button onClick={() => { const t = prompt("Ab wann bist du da? (z.B. 10:30)", "10:00"); if (t && /^\d{2}:\d{2}$/.test(t)) submitSelfOverride("late", { start_time: t }); }}
              disabled={selfOvSaving}
              style={{ padding: "10px 18px", background: "#F59E0B", color: "white", border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 14 }}>
              ⏰ Verspätung
            </button>
            <button onClick={() => { const t = prompt("Bis wann bleibst du heute? (z.B. 14:00)", "14:00"); if (t && /^\d{2}:\d{2}$/.test(t)) submitSelfOverride("early_leave", { end_time: t }); }}
              disabled={selfOvSaving}
              style={{ padding: "10px 18px", background: "var(--cardHover)", color: "var(--text)", border: "1px solid var(--border)", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 14 }}>
              🚪 Früher Schluss
            </button>
          </div>
          {selfOvMsg && <div style={{ marginTop: 10, padding: "8px 12px", background: "var(--cardHover)", borderRadius: 8, fontSize: 13 }}>{selfOvMsg}</div>}
        </div>
      )}

      {/* SICK_DAY_NOTIFY_v1 (5.3) -- Self-Sicht: eigene Termine durchgehen nach Krankmeldung */}
      {selfSickModal && (
        <SickDayNotifyModal
          employeeId={selfSickModal.employeeId}
          employeeName={selfSickModal.employeeName}
          date={selfSickModal.date}
          employees={selfEmployees}
          mode="self"
          onClose={() => setSelfSickModal(null)}
          onChanged={() => {}}
        />
      )}

      {/* SETTINGS_MASONRY_v1 -- wie auf der Einstellungen-Seite: Karten so hoch wie ihr Inhalt */}
      <div className="settings-masonry" style={{ columnCount: window.innerWidth < 768 ? 1 : 2, columnGap: 24 }}>
        <div style={{ background: "var(--card)", borderRadius: 12, padding: 24, border: "1px solid var(--border)" }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Persönliche Daten</h3>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Name</label>
            <input style={inputStyle} value={currentUser?.name || ""} readOnly />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>E-Mail</label>
            <input style={inputStyle} value={currentUser?.email || ""} readOnly />
          </div>
          <div>
            <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Rolle</label>
            <input style={inputStyle} value={currentUser?.role || ""} readOnly />
          </div>
        </div>
        {/* THEME_TOGGLE_UI_v1: Theme-Toggle Card */}
        <div style={{ background: "var(--card)", borderRadius: 12, padding: 24, border: "1px solid var(--border)" }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Erscheinungsbild</h3>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0" }}>
            <div>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Theme</div>
              <div style={{ fontSize: 13, color: "var(--textLight)" }}>{theme === "dark" ? "Dunkles Design (Standard)" : "Helles Design"}</div>
            </div>
            <button onClick={toggleTheme} style={{ padding: "10px 18px", background: "var(--bg)", color: "var(--text)", border: "1px solid var(--border)", borderRadius: 8, cursor: "pointer", fontSize: 14, fontWeight: 600 }}>
              {theme === "dark" ? "☼ Auf Hell wechseln" : "☾ Auf Dunkel wechseln"}
            </button>
          </div>
          {/* PROFILE_APPEARANCE_v1 (M1): Farbthema auch im Profil -- mobil von überall erreichbar */}
          <div style={{ borderTop: "1px solid var(--border)", marginTop: 12, paddingTop: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Farbthema</div>
            <div style={{ fontSize: 13, color: "var(--textLight)", marginBottom: 10 }}>Akzentfarbe der Oberfläche</div>
            <div style={{ display: "flex", gap: 10 }}>
              {[
                { key: "purple",  label: "Lila",    color: "#7C3AED" },
                { key: "gold",    label: "Gold",    color: "#D4AF6E" },
                { key: "schwarz", label: "Schwarz", color: "#1F2937" },
                { key: "grau",    label: "Grau",    color: "#6B7280" },
              ].map(t => {
                const cur = (() => { try { const v = localStorage.getItem("shearly_color_theme"); return ["purple","gold","schwarz","grau"].includes(v) ? v : "purple"; } catch { return "purple"; } })();
                const isActive = cur === t.key;
                return (
                  <button key={t.key} title={t.label} aria-label={"Farbthema " + t.label}
                    onClick={() => { try { localStorage.setItem("shearly_color_theme", t.key); } catch(e) {} window.location.reload(); }}
                    style={{
                      width: 34, height: 34, borderRadius: "50%", background: t.color,
                      border: isActive ? "3px solid var(--text)" : "1px solid var(--border)",
                      cursor: "pointer", padding: 0, flexShrink: 0,
                    }} />
                );
              })}
            </div>
          </div>
        </div>
        <div style={{ background: "var(--card)", borderRadius: 12, padding: 24, border: "1px solid var(--border)" }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Passwort ändern</h3>
          {msg && <div style={{ padding: 10, borderRadius: 8, marginBottom: 12, background: msgType === "success" ? "rgba(16,185,129,0.12)" : "rgba(220,38,38,0.12)", border: "1px solid " + (msgType === "success" ? "rgba(16,185,129,0.35)" : "rgba(220,38,38,0.35)"), color: msgType === "success" ? "#10B981" : "#EF4444", fontSize: 13 }}>{msg}</div>}
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Aktuelles Passwort</label>
            <input type="password" style={inputStyle} value={oldPw} onChange={e => setOldPw(e.target.value)} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Neues Passwort</label>
            <input type="password" style={inputStyle} value={newPw} onChange={e => setNewPw(e.target.value)} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 4 }}>Passwort bestätigen</label>
            <input type="password" style={inputStyle} value={confirmPw} onChange={e => setConfirmPw(e.target.value)} />
          </div>
          <button onClick={handleChangePw} style={{ width: "100%", padding: 12, background: COLORS.primary, color: "white", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}>
            Passwort ändern
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// PAGE: Settings
// ============================================================

    // ==============================
    // COMPONENT: Team Account Card
    // ==============================
    // DATA_ACT_SELF_EXPORT_v1 -- EU Data Act: Salon laedt ALLE eigenen Daten selbst als ZIP/CSV.
    // Nutzt GET /api/my-salon/export (salon_id aus Session, owner-only). Roher fetch wegen Binaer-Download.
    export const DataExportCard = () => {
        const [busy, setBusy] = useState(false);
        const [msg, setMsg] = useState("");
        const [err, setErr] = useState("");
        const downloadExport = async () => {
            setBusy(true); setMsg(""); setErr("");
            try {
                const tok = api.token || sessionStorage.getItem("sp_token");
                const res = await fetch(API_BASE + "/my-salon/export", {
                    headers: { "Authorization": "Bearer " + tok }
                });
                if (!res.ok) {
                    let detail = "Status " + res.status;
                    try { const j = await res.json(); if (j && j.error) detail = j.error; } catch (e) {}
                    setErr("Export fehlgeschlagen: " + detail);
                    setBusy(false);
                    return;
                }
                const rows = res.headers.get("X-Shearly-Rows");
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                const ts = new Date().toISOString().slice(0, 10);
                a.href = url; a.download = "shearly-datenexport-" + ts + ".zip"; a.click();
                setTimeout(() => URL.revokeObjectURL(url), 1500);
                setMsg("Export erstellt ✓" + (rows ? (" — " + rows + " Datensätze") : ""));
                setTimeout(() => setMsg(""), 8000);
            } catch (e) {
                setErr("Netzwerkfehler: " + (e.message || "unbekannt"));
            } finally {
                setBusy(false);
            }
        };
        return (
            <Card>
                <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700 }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                        <Download size={18} color={COLORS.primary} /> Meine Daten exportieren
                    </span>
                </h3>
                <p style={{ fontSize: 12, color: COLORS.textLight, margin: "0 0 12px", lineHeight: 1.7 }}>
                    Lade jederzeit <strong>alle Daten deines Salons</strong> (Kunden, Termine, Leistungen,
                    Mitarbeiter, Rechnungen) als ZIP-Datei mit CSV-Tabellen herunter. Das Format ist offen und
                    lässt sich in anderer Software einlesen — du bist nie an <Wm /> gebunden
                    (EU Data Act / DSGVO Art. 20).
                </p>
                <button onClick={downloadExport} disabled={busy} style={{
                    padding: "10px 18px", borderRadius: 8, border: "none",
                    background: busy ? COLORS.textLight : COLORS.primary, color: "#fff",
                    cursor: busy ? "default" : "pointer", fontSize: 13, fontWeight: 600,
                    display: "inline-flex", alignItems: "center", gap: 8
                }}>
                    <Download size={14} /> {busy ? "Export wird erstellt…" : "Daten als ZIP herunterladen"}
                </button>
                {msg && <div style={{ marginTop: 10, fontSize: 12, color: COLORS.success, fontWeight: 600 }}>{msg}</div>}
                {err && <div style={{ marginTop: 10, fontSize: 12, color: COLORS.danger, fontWeight: 600 }}>{err}</div>}
            </Card>
        );
    };

    export const TeamAccountCard = () => {
        const [teamEmail, setTeamEmail] = useState("");
        const [teamPw, setTeamPw] = useState("");
        const [teamPw2, setTeamPw2] = useState("");   // TEAM_PW_CONFIRM_v1 -- zweite Eingabe zur Kontrolle
        const [showPw, setShowPw] = useState(false);  // TEAM_PW_CONFIRM_v1 -- Passwort sichtbar machen
        const [teamExists, setTeamExists] = useState(false);
        const [teamLoading, setTeamLoading] = useState(true);
        const [teamMsg, setTeamMsg] = useState("");
        const [teamError, setTeamError] = useState("");
        const [editing, setEditing] = useState(false);  // TEAM_ACCOUNT_SUMMARY_v1 -- nach Einrichtung kompakte Status-Ansicht; Formular nur beim Bearbeiten

        useEffect(() => {
            api.request("/salon/team-account").then(res => {
                if (res.ok && res.data.exists) {
                    setTeamExists(true);
                    setTeamEmail(res.data.email || "");
                }
                setTeamLoading(false);
            }).catch(() => setTeamLoading(false));
        }, []);

        const saveTeam = async () => {
            setTeamMsg(""); setTeamError("");
            if (!teamEmail || !teamPw) { setTeamError("E-Mail und Passwort erforderlich"); return; }
            if (teamPw.length < 8) { setTeamError("Passwort mind. 8 Zeichen + 1 Zahl/Sonderzeichen"); return; }
            if (teamPw !== teamPw2) { setTeamError("Die beiden Passwörter stimmen nicht überein"); return; }
            const res = await api.request("/salon/team-account", {
                method: "POST",
                body: JSON.stringify({ email: teamEmail, password: teamPw })
            });
            if (res.ok && res.data.success) {
                setTeamMsg(res.data.message);
                setTeamExists(true);
                setTeamPw(""); setTeamPw2("");
                setEditing(false);   // TEAM_ACCOUNT_SUMMARY_v1 -- zurueck zur klaren Status-Ansicht
            } else {
                setTeamError(res.data?.error || "Fehler beim Speichern");
            }
        };

        if (teamLoading) return null;

        // TEAM_ACCOUNT_SUMMARY_v1 -- ist der Account eingerichtet, zeigen wir eine klare Status-Karte
        // statt des leeren Formulars. Sonst sieht es fuer den Inhaber so aus, als waere nichts eingerichtet
        // (leere Passwortfelder = "noch nicht gemacht"-Gefuehl). Bearbeiten erst auf Klick.
        if (teamExists && !editing) {
            return React.createElement(Card, { style: { marginBottom: 16 } },
                React.createElement("h3", { style: { margin: "0 0 4px", fontSize: 15, fontWeight: 700 } },
                    React.createElement("span", { style: { display: "inline-flex", alignItems: "center", gap: 8 } },
                        React.createElement(Users, { size: 18 }),
                        "Team-Account (Empfang)"
                    )
                ),
                React.createElement("p", { style: { fontSize: 12, color: COLORS.textLight, margin: "0 0 12px" } },
                    "Shared Login für alle Mitarbeiter am Empfang. Rolle: Mitarbeiter."
                ),
                React.createElement("div", { style: { padding: "12px 14px", background: COLORS.success + "15", border: "1px solid " + COLORS.success + "55", borderRadius: 8, marginBottom: 12 } },
                    React.createElement("div", { style: { fontSize: 13, fontWeight: 700, color: COLORS.success, display: "flex", alignItems: "center", gap: 6, marginBottom: 6 } },
                        React.createElement(Check, { size: 16 }),
                        "Team-Account ist eingerichtet"
                    ),
                    React.createElement("div", { style: { fontSize: 13, color: COLORS.text } },
                        "Login-E-Mail: ",
                        React.createElement("strong", null, teamEmail || "—")
                    ),
                    React.createElement("div", { style: { fontSize: 11, color: COLORS.textLight, marginTop: 4 } },
                        "Passwort ist gesetzt und aus Sicherheitsgründen ausgeblendet."
                    )
                ),
                teamMsg && React.createElement("div", { style: { color: COLORS.success, fontSize: 12, marginBottom: 8 } }, teamMsg),
                React.createElement(Btn, { onClick: () => { setEditing(true); setTeamMsg(""); setTeamError(""); }, size: "sm", variant: "secondary" },
                    "Zugangsdaten ändern"
                )
            );
        }

        return React.createElement(Card, { style: { marginBottom: 16 } },
            React.createElement("h3", { style: { margin: "0 0 4px", fontSize: 15, fontWeight: 700 } },
                React.createElement("span", { style: { display: "inline-flex", alignItems: "center", gap: 8 } },
                    React.createElement(Users, { size: 18 }),
                    "Team-Account (Empfang)"
                )
            ),
            React.createElement("p", { style: { fontSize: 12, color: COLORS.textLight, margin: "0 0 12px" } },
                "Shared Login für alle Mitarbeiter am Empfang. Rolle: Mitarbeiter."
            ),
            teamExists && React.createElement("div", { style: { padding: "8px 12px", background: COLORS.success + "15", borderRadius: 6, marginBottom: 12, fontSize: 12 } },
                "\u2705 Team-Account aktiv"
            ),
            React.createElement("div", { style: { marginBottom: 8 } },
                React.createElement("label", { style: { fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 } }, "E-Mail"),
                React.createElement("input", {
                    type: "email",
                    value: teamEmail,
                    onChange: e => setTeamEmail(e.target.value),
                    placeholder: "empfang@salon.de",
                    style: { width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid " + COLORS.border, fontSize: 13, boxSizing: "border-box" }
                })
            ),
            React.createElement("div", { style: { marginBottom: 8 } },
                React.createElement("label", { style: { fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 } }, teamExists ? "Neues Passwort" : "Passwort"),
                React.createElement("div", { style: { position: "relative" } },
                    React.createElement("input", {
                        type: showPw ? "text" : "password",
                        value: teamPw,
                        onChange: e => setTeamPw(e.target.value),
                        placeholder: teamExists ? "Neues Passwort setzen" : "Mind. 8 Zeichen + 1 Zahl/Sonderzeichen",
                        style: { width: "100%", padding: "8px 38px 8px 10px", borderRadius: 8, border: "1px solid " + COLORS.border, fontSize: 13, boxSizing: "border-box" }
                    }),
                    React.createElement("button", {
                        type: "button",
                        onClick: () => setShowPw(s => !s),
                        title: showPw ? "Passwort verbergen" : "Passwort anzeigen",
                        style: { position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", padding: 4, display: "flex", color: showPw ? COLORS.primary : COLORS.textLight }
                    }, React.createElement(Eye, { size: 16 }))
                )
            ),
            React.createElement("div", { style: { marginBottom: 12 } },
                React.createElement("label", { style: { fontSize: 12, fontWeight: 600, display: "block", marginBottom: 4 } }, teamExists ? "Neues Passwort wiederholen" : "Passwort wiederholen"),
                React.createElement("input", {
                    type: showPw ? "text" : "password",
                    value: teamPw2,
                    onChange: e => setTeamPw2(e.target.value),
                    placeholder: "Zur Kontrolle erneut eingeben",
                    style: { width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid " + COLORS.border, fontSize: 13, boxSizing: "border-box" }
                }),
                teamPw2 && teamPw !== teamPw2 ? React.createElement("div", { style: { color: COLORS.danger, fontSize: 11, marginTop: 4 } }, "Stimmt noch nicht überein") : null,
                teamPw2 && teamPw === teamPw2 ? React.createElement("div", { style: { color: COLORS.success, fontSize: 11, marginTop: 4 } }, "✓ Passwörter stimmen überein") : null
            ),
            teamError && React.createElement("div", { style: { color: COLORS.danger, fontSize: 12, marginBottom: 8 } }, teamError),
            teamMsg && React.createElement("div", { style: { color: COLORS.success, fontSize: 12, marginBottom: 8 } }, teamMsg),
            React.createElement("div", { style: { display: "flex", gap: 8 } },
                React.createElement(Btn, { onClick: saveTeam, size: "sm" },
                    React.createElement(Check, { size: 14 }),
                    " ",
                    teamExists ? "Team-Account aktualisieren" : "Team-Account erstellen"
                ),
                // TEAM_ACCOUNT_SUMMARY_v1 -- bei bestehendem Account: Bearbeiten abbrechen, zurueck zur Status-Ansicht
                teamExists && React.createElement(Btn, { onClick: () => { setEditing(false); setTeamPw(""); setTeamPw2(""); setTeamError(""); }, size: "sm", variant: "secondary" },
                    "Abbrechen"
                )
            )
        );
    };


    // SALON_LOGO_v1 -- Logo-Upload-Component, top-level (kein inline-Pattern).
    // Use-Case: Salon-Inhaber kann eigenes Logo hochladen — erscheint im Booking-Form, kann zukünftig in SMS verlinkt werden.
    // KASSE_HEUTE_v1 (Toby 14.10.2026) -- wer den Tagesstand der Shearly Kasse im Dashboard sieht.
    // Inhaber immer; je weiterem Login ein Schalter, Standard an. Nur bei Salons mit Shearly Kasse.
    export const KasseDayTotalsCard = () => {
        const [data, setData] = useState(null);
        const [busy, setBusy] = useState(null);
        const [err, setErr] = useState("");
        useEffect(() => {
            api.request("/settings/kasse-day-totals").then(r => { if (r && r.ok) setData(r.data); }).catch(() => {});
        }, []);
        if (!data || !data.pos_shearly) return null;
        const toggle = async (l) => {
            setBusy(l.user_id); setErr("");
            const r = await api.request("/settings/kasse-day-totals", { method: "PUT", body: JSON.stringify({ user_id: l.user_id, visible: !l.visible }) });
            setBusy(null);
            if (r && r.ok) setData(d => ({ ...d, logins: d.logins.map(x => x.user_id === l.user_id ? { ...x, visible: !l.visible } : x) }));
            else setErr((r && r.data && r.data.error) || "Speichern hat nicht geklappt, bitte noch einmal versuchen.");
        };
        return (
            <Card style={{ marginBottom: 16 }}>
                <h3 style={{ margin: "0 0 6px", fontSize: 15, fontWeight: 700 }}>Kasse heute im Dashboard</h3>
                <p style={{ margin: "0 0 10px", fontSize: 12, color: COLORS.textLight, lineHeight: 1.5 }}>
                    Das Dashboard zeigt, was heute in der <Wm /> Kasse bar, mit Karte und sonst bezahlt wurde (mit Trinkgeld). Du siehst das immer. Hier stellst du ein, welche Logins es außerdem sehen.
                </p>
                {data.logins.length === 0 ? (
                    <div style={{ fontSize: 13, color: COLORS.textLight }}>Es gibt noch keine weiteren Logins.</div>
                ) : data.logins.map(l => (
                    <label key={l.user_id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "8px 0", borderBottom: `1px solid ${COLORS.border}`, cursor: "pointer", fontSize: 13 }}>
                        <span>{l.name}{l.team ? <span style={{ color: COLORS.textLight }}> · Team-Account</span> : null}</span>
                        <input type="checkbox" checked={!!l.visible} disabled={busy === l.user_id} onChange={() => toggle(l)}
                               style={{ accentColor: COLORS.primary, width: 18, height: 18, cursor: "pointer" }} />
                    </label>
                ))}
                {err && <div style={{ color: COLORS.danger, fontSize: 12, marginTop: 8 }}>{err}</div>}
            </Card>
        );
    };

    export const SalonLogoUpload = ({ currentUser, salonProfile, setSalonProfile }) => {
      const [uploading, setUploading] = useState(false);
      const [msg, setMsg] = useState("");
      const fileRef = useRef(null);
      const salonId = currentUser && currentUser.salon_id;
      const handleFile = async (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file || !salonId) return;
        if (file.size > 10 * 1024 * 1024) { setMsg("Datei zu groß (max 10 MB)"); return; }
        setUploading(true); setMsg("");
        const fd = new FormData();
        fd.append("image", file);
        try {
          const res = await fetch("/api/salons/" + salonId + "/logo", {
            method: "POST", body: fd,
            headers: { Authorization: "Bearer " + (sessionStorage.getItem("sp_token") || "") }
          });
          const data = await res.json();
          if (res.ok && data.logo_url) {
            setSalonProfile(p => ({ ...p, logo_url: data.logo_url }));
            setMsg("✓ Logo hochgeladen");
            setTimeout(() => setMsg(""), 2000);
          } else {
            setMsg("Fehler: " + (data.error || "unbekannt"));
          }
        } catch (err) {
          setMsg("Upload fehlgeschlagen");
        } finally {
          setUploading(false);
          if (fileRef.current) fileRef.current.value = "";
        }
      };
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 16, padding: 12, background: "var(--cardHover)", borderRadius: 8, marginBottom: 16 }}>
          <div style={{
            width: 80, height: 80, borderRadius: 12, overflow: "hidden",
            background: "var(--card)", border: "2px dashed " + COLORS.border,
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}>
            {salonProfile.logo_url ? (
              <img src={salonProfile.logo_url} alt="Logo" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
            ) : (
              <span style={{ fontSize: 24, opacity: 0.4 }}>🏪</span>
            )}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>Salon-Logo</div>
            <div style={{ fontSize: 11, color: COLORS.textLight, marginBottom: 8 }}>
              Wird im Online-Buchungs-Formular und auf der KI-Sichtbarkeits-Seite verwendet. Empfohlen: quadratisch, mind. 200×200px.
            </div>
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic" onChange={handleFile} style={{ display: "none" }} />
            <button onClick={() => fileRef.current && fileRef.current.click()} disabled={uploading}
              style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid " + COLORS.border, background: "transparent", color: COLORS.text, fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
              {uploading ? "Lade hoch..." : (salonProfile.logo_url ? "Logo ändern" : "Logo hochladen")}
            </button>
            {msg && <span style={{ marginLeft: 10, fontSize: 11, color: msg.startsWith("✓") ? "#10B981" : "#EF4444" }}>{msg}</span>}
          </div>
        </div>
      );
    };

    // BOOKING_V2_KOPF_v1 (Toby 29.10.2026 „Ja, Salonfoto“) -- Foto oben auf der Online-Buchungsseite.
    // Hochladen wie beim Logo (SALON_LOGO_v1); der Server verkleinert und entfernt die Bild-Metadaten (Standort).
    export const SalonHeaderUpload = ({ salonId, salonProfile, setSalonProfile, salonSlug }) => {
      const [busy, setBusy] = useState(false);
      const [msg, setMsg] = useState("");
      const fileRef = useRef(null);
      const url = salonProfile.booking_header_url || "";
      const flash = (t) => { setMsg(t); setTimeout(() => setMsg(""), 3500); };
      const handleFile = async (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file || !salonId) return;
        if (file.size > 15 * 1024 * 1024) { setMsg("Datei zu groß (höchstens 15 MB)"); return; }
        setBusy(true); setMsg("");
        const fd = new FormData();
        fd.append("image", file);
        try {
          const res = await fetch("/api/salons/" + salonId + "/header-image", {
            method: "POST", body: fd,
            headers: { Authorization: "Bearer " + (sessionStorage.getItem("sp_token") || "") }
          });
          const data = await res.json().catch(() => ({}));
          if (res.ok && data.booking_header_url) {
            setSalonProfile(p => ({ ...p, booking_header_url: data.booking_header_url }));
            flash("✓ Foto gespeichert – die Buchungsseite zeigt es ab dem nächsten Öffnen");
          } else {
            setMsg("Fehler: " + (data.error || "unbekannt"));
          }
        } catch (err) {
          setMsg("Hochladen fehlgeschlagen");
        } finally {
          setBusy(false);
          if (fileRef.current) fileRef.current.value = "";
        }
      };
      const remove = async () => {
        if (!salonId || !url) return;
        if (!window.confirm("Foto von der Buchungsseite entfernen? Oben steht dann wieder der Farbverlauf.")) return;
        setBusy(true); setMsg("");
        const res = await api.request("/salons/" + salonId + "/header-image", { method: "DELETE" });
        setBusy(false);
        if (res && res.ok) { setSalonProfile(p => ({ ...p, booking_header_url: "" })); flash("✓ Foto entfernt"); }
        else setMsg("Fehler beim Entfernen");
      };
      return (
        <Card>
          <h3 style={{ margin: "0 0 4px", fontSize: 15, fontWeight: 700 }}>🖼 Foto oben auf der Buchungsseite</h3>
          <div style={{ fontSize: 12, color: "var(--textLight)", marginBottom: 12, lineHeight: 1.5 }}>
            Ein Bild von deinem Salon macht die Buchungsseite persönlich. Am besten ein helles Foto im Querformat, mindestens 1200 Pixel breit.
            Bitte nur Fotos, an denen du die Rechte hast und auf denen keine Kundinnen oder Kunden zu erkennen sind.
          </div>
          <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
            <div data-booking-header-preview="1" style={{ width: 220, maxWidth: "100%", aspectRatio: "16 / 9", borderRadius: 10, overflow: "hidden", flexShrink: 0,
                          border: url ? "1px solid var(--border)" : "2px dashed " + COLORS.border, background: "var(--cardHover)",
                          display: "flex", alignItems: "center", justifyContent: "center" }}>
              {url ? <img src={url} alt="Salonfoto" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 40%" }} />
                   : <span style={{ fontSize: 12, color: COLORS.textLight }}>noch kein Foto</span>}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-start" }}>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic" onChange={handleFile} style={{ display: "none" }} />
              <button type="button" onClick={() => fileRef.current && fileRef.current.click()} disabled={busy}
                style={{ padding: "7px 14px", borderRadius: 8, border: "1px solid " + COLORS.border, background: "transparent", color: COLORS.text, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                {busy ? "Einen Moment …" : (url ? "Foto ändern" : "Foto hochladen")}
              </button>
              {url && !busy && (
                <button type="button" onClick={remove}
                  style={{ padding: 0, border: 0, background: "transparent", color: COLORS.danger || "#EF4444", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                  Foto entfernen
                </button>
              )}
              {salonSlug && <a href={"/b/" + salonSlug} target="_blank" rel="noopener" style={{ fontSize: 12, fontWeight: 600, color: "var(--primary, #7C3AED)" }}>Buchungsseite ansehen ↗</a>}
            </div>
          </div>
          {msg && <div style={{ marginTop: 10, fontSize: 12, fontWeight: 600, color: msg.startsWith("✓") ? "#22c55e" : "#ef4444" }}>{msg}</div>}
        </Card>
      );
    };

    export const SettingsPage = () => {
  const [stripeStatus, setStripeStatus] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);

  // BUFFER_v1 + SICK_DAY_NOTIFY_v1 -- Salon-Settings (Pufferzeit, Notfall-Nummer)
  const _ctxUser = React.useContext(UserContext);
  const _salonId = _ctxUser && _ctxUser.salon_id;
  const [bufferMin, setBufferMin] = useState(null); // null = noch nicht geladen
  // SEASONAL_DECOR_v1 -- Schalter "Saisonale Deko im Kalender" (salons.seasonal_decor_enabled, Default AN)
  const [seasonalDecor, setSeasonalDecor] = useState(true);
  const [seasonalMsg, setSeasonalMsg] = useState("");
  const toggleSeasonalDecor = async () => {
    const next = !seasonalDecor;
    setSeasonalDecor(next); setSeasonalMsg("");
    const res = await api.request("/salons/" + _salonId, { method: "PUT", body: JSON.stringify({ seasonal_decor_enabled: next ? 1 : 0 }) });
    if (res && res.ok) setSeasonalMsg("Gespeichert — gilt nach dem nächsten Öffnen des Kalenders");
    else { setSeasonalDecor(!next); setSeasonalMsg("Fehler beim Speichern"); }
    setTimeout(() => setSeasonalMsg(""), 3000);
  };
  // BOOKING_THEME_v1 (Toby 08.10.2026) -- Farbe der Online-Buchungsseite (salons.booking_theme)
  const BOOKING_THEMES = [
    { k: "violett", name: <><Wm /> Violett</>, sub: "Standard", c: ["#7C3AED", "#A78BFA", "#D4A574", "#FAF9F7"] },
    { k: "salbei",  name: "Salbei",          sub: "Grün & Holz", c: ["#5C7F62", "#8DAA92", "#D4A96A", "#FAF7F0"] },
    { k: "gold",    name: "Schwarz & Gold",  sub: "klassisch", c: ["#2A2622", "#B08D57", "#E6D6BA", "#F8F6F2"] },
    { k: "rose",    name: "Altrosa",         sub: "warm",      c: ["#A4566A", "#D49AA8", "#C9A27E", "#FBF7F6"] },
    { k: "blau",    name: "Nachtblau",       sub: "ruhig",     c: ["#2F5D8A", "#7FA3C6", "#C8A46B", "#F6F8FA"] },
  ];
  const [bookingTheme, setBookingTheme] = useState("violett");
  const [salonSlug, setSalonSlug] = useState("");
  const [themeMsg, setThemeMsg] = useState("");
  const chooseBookingTheme = async (k) => {
    if (k === bookingTheme) return;
    const prev = bookingTheme;
    setBookingTheme(k); setThemeMsg("");
    const res = await api.request("/salons/" + _salonId, { method: "PUT", body: JSON.stringify({ booking_theme: k }) });
    if (res && res.ok) setThemeMsg("Gespeichert — die Buchungsseite zeigt die Farbe ab dem nächsten Öffnen");
    else { setBookingTheme(prev); setThemeMsg("Fehler beim Speichern"); }
    setTimeout(() => setThemeMsg(""), 4000);
  };
  const [bufferSaving, setBufferSaving] = useState(false);
  const [escalationPhone, setEscalationPhone] = useState(""); // SICK_DAY_NOTIFY_v1
  const [escalationSaving, setEscalationSaving] = useState(false);
  const [escalationMsg, setEscalationMsg] = useState("");
  // SMS_SIGNATURE_v1 -- pro Salon konfigurierbare Signatur
  const [smsSig, setSmsSig] = useState("");
  const [smsSigSaving, setSmsSigSaving] = useState(false);
  const [smsSigMsg, setSmsSigMsg] = useState("");
  // SALON_PROFILE_SAVE_v1 -- Salon-Profil-Form wirklich speichern (vorher: Placeholder ohne State)
  const [salonProfile, setSalonProfile] = useState({
    name: "", address: "", phone: "", email: "",
    opening_time: "08:00", closing_time: "20:00", buffer_minutes: 0,
    logo_url: "",  // SALON_LOGO_v1
    booking_header_url: "",  // BOOKING_V2_KOPF_v1
  });
  const currentUser = React.useContext(UserContext);
  const [salonProfileSaving, setSalonProfileSaving] = useState(false);
  const [salonProfileMsg, setSalonProfileMsg] = useState("");
  const updSalonField = (field, value) => setSalonProfile(p => ({ ...p, [field]: value }));
  // OPENING_TIME_ARROWS_v1 -- Pfeiltasten ↑/↓ verstellen Öffnung/Schluss (±5 Min), wie SHIFT_TIME_ARROWS_v1
  // (Helfer dort sind lokal in der Schichtplan-Komponente gescoped, daher eigene Kopie)
  const stepTimeStr = (hhmm, deltaMin) => {
    const m = /^(\d{1,2}):(\d{2})/.exec(hhmm || "");
    let total = m ? (parseInt(m[1], 10) * 60 + parseInt(m[2], 10)) : 0;
    total = ((total + deltaMin) % 1440 + 1440) % 1440;
    return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
  };
  const timeArrowKey = (e, val, field) => {
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
      updSalonField(field, stepTimeStr(val, e.key === "ArrowUp" ? 5 : -5));
    }
  };
  // OWNER_SAVE_AUDIT_v1 -- No-Show / Loyalty: vorher reine defaultValue-Felder ohne Speichern.
  const [noShow, setNoShow] = useState({ prepay_on: false, prepay_threshold: 100, prepay_pct: 100, prepay_timeout: 15, sumup_ready: false, fee: 25, cancel_h: 12, online_cancel_on: true,
    prepay_provider: "sumup", paypal_client_id: "", paypal_client_secret: "", paypal_has_secret: false, paypal_sandbox: false, paypal_ready: false,
    stripe_prepay_secret_key: "", stripe_prepay_has_key: false, stripe_ready: false, stripe_livemode: null /* PREPAY_STRIPE_v1 */ }); // BOOKING_PREPAY_v1 + PREPAY_PAYPAL_v1
  const [paypalTestMsg, setPaypalTestMsg] = useState("");
  const [paypalTesting, setPaypalTesting] = useState(false);
  /* PREPAY_STRIPE_v1 -- Stripe-Schluessel des Salons speichern + Verbindung testen (wie PayPal) */
  const [stripeTestMsg, setStripeTestMsg] = useState("");
  const [stripeTesting, setStripeTesting] = useState(false);
  const testStripe = async () => {
    if (_salonId == null) return;
    setStripeTesting(true); setStripeTestMsg("");
    const body = { prepay_provider: "stripe" };
    if (noShow.stripe_prepay_secret_key) body.stripe_prepay_secret_key = noShow.stripe_prepay_secret_key;
    const sv = await api.request("/salons/" + _salonId, { method: "PUT", body: JSON.stringify(body) });
    if (!(sv && sv.ok)) { setStripeTestMsg((sv && sv.data && sv.data.error) || "Speichern fehlgeschlagen"); setStripeTesting(false); return; }
    const r = await api.request("/owner/stripe-prepay/test", { method: "POST", body: "{}" });
    const ok = !!(r && r.ok && r.data && r.data.ok);
    setStripeTestMsg(ok ? ("Stripe verbunden ✓" + (r.data.livemode === false ? " (Testmodus-Schlüssel)" : "")) : ((r && r.data && r.data.error) || "Verbindung fehlgeschlagen"));
    setNoShow(p => ({ ...p, stripe_ready: ok, stripe_livemode: ok ? r.data.livemode : p.stripe_livemode, stripe_prepay_has_key: p.stripe_prepay_has_key || !!p.stripe_prepay_secret_key, stripe_prepay_secret_key: "" }));
    setStripeTesting(false);
  };
  // PREPAY_PAYPAL_v1 -- Zugangsdaten speichern (Secret nur wenn eingegeben) und dann Verbindung testen
  const testPaypal = async () => {
    if (_salonId == null) return;
    setPaypalTesting(true); setPaypalTestMsg("");
    const body = { prepay_provider: "paypal", paypal_client_id: noShow.paypal_client_id || "", paypal_sandbox: !!noShow.paypal_sandbox };
    if (noShow.paypal_client_secret) body.paypal_client_secret = noShow.paypal_client_secret;
    const s = await api.request("/salons/" + _salonId, { method: "PUT", body: JSON.stringify(body) });
    if (!s || !s.ok) { setPaypalTestMsg("Speichern fehlgeschlagen"); setPaypalTesting(false); return; }
    const r = await api.request("/owner/paypal/test", { method: "POST", body: "{}" });
    const ok = !!(r && r.ok && r.data && r.data.ok);
    setPaypalTestMsg(ok ? "PayPal verbunden ✓" : ((r && r.data && r.data.error) || "Verbindung fehlgeschlagen"));
    setNoShow(p => ({ ...p, paypal_ready: ok, paypal_has_secret: p.paypal_has_secret || !!p.paypal_client_secret, paypal_client_secret: "" }));
    setPaypalTesting(false);
  };
  const [noShowMsg, setNoShowMsg] = useState("");
  const [loyaltyOn, setLoyaltyOn] = useState(false);
  const [loyaltyMsg, setLoyaltyMsg] = useState("");
  // LOYALTY_ENGINE_v1 -- Punkte-Regeln pro Salon (jetzt mit echtem Backend)
  // LOYALTY_TIER_REWARDS_v1 -- Einloesen laeuft ueber Stufen-Rabatte, redeem_ratio entfaellt
  const [loyaltyRules, setLoyaltyRules] = useState({ per_euro: 1, per_visit: 10 });
  useEffect(() => {
    if (!_salonId) return;
    api.request("/salons/" + _salonId).then(res => {
      if (res && res.ok && res.data) {
        const v = res.data.buffer_minutes;
        setBufferMin(typeof v === "number" ? v : 0);
        setSeasonalDecor(!(res.data.seasonal_decor_enabled === 0 || res.data.seasonal_decor_enabled === false)); // SEASONAL_DECOR_v1
        setBookingTheme(["violett", "salbei", "gold", "rose", "blau"].includes(res.data.booking_theme) ? res.data.booking_theme : "violett"); // BOOKING_THEME_v1
        setSalonSlug(res.data.slug || "");
        setEscalationPhone(res.data.escalation_phone || "");
        setSmsSig(res.data.sms_signature || "");
        // OWNER_SAVE_AUDIT_v1
        // BOOKING_PREPAY_v1 -- echte Online-Vorkasse (SumUp) ersetzt die alte, wirkungslose "Vorauszahlung (%)"
        // (noshow_prepayment_pct wurde nirgends ausgewertet). Schwelle NULL/0 = aus.
        const _thr = res.data.prepay_threshold_eur != null ? Number(res.data.prepay_threshold_eur) : 0;
        setNoShow({
          // PREPAY_FIELDS_VISIBLE_v1 -- eigener Schalter (Migration 069); Schwelle bleibt auch bei AUS gespeichert
          prepay_on: (res.data.prepay_enabled === 1 || res.data.prepay_enabled === true) && _thr > 0,
          prepay_threshold: _thr > 0 ? _thr : 100,
          prepay_pct: Number(res.data.prepay_pct != null ? res.data.prepay_pct : 100) || 100,
          prepay_timeout: Number(res.data.prepay_timeout_min != null ? res.data.prepay_timeout_min : 15) || 15,
          sumup_ready: String(res.data.pos_provider || "").toLowerCase() === "sumup" && !!res.data.pos_connected_at,
          // PREPAY_PAYPAL_v1
          prepay_provider: res.data.prepay_provider === "paypal" ? "paypal" : res.data.prepay_provider === "stripe" ? "stripe" : "sumup",
          paypal_client_id: res.data.paypal_client_id || "",
          paypal_client_secret: "",
          paypal_has_secret: !!res.data.paypal_has_secret,
          paypal_sandbox: !!(res.data.paypal_sandbox === 1 || res.data.paypal_sandbox === true),
          paypal_ready: !!res.data.paypal_connected_at && !!res.data.paypal_client_id && !!res.data.paypal_has_secret,
          stripe_prepay_secret_key: "", /* PREPAY_STRIPE_v1 */
          stripe_prepay_has_key: !!res.data.stripe_prepay_has_key,
          stripe_ready: !!res.data.stripe_prepay_connected_at && !!res.data.stripe_prepay_has_key,
          stripe_livemode: res.data.stripe_prepay_livemode == null ? null : !!res.data.stripe_prepay_livemode,
          fee: Number(res.data.noshow_fee || 0) || 0,
          cancel_h: Number(res.data.cancellation_hours != null ? res.data.cancellation_hours : 12),
          online_cancel_on: res.data.online_cancellation_enabled !== false,  // ONLINE_CANCEL_v1
          online_reschedule_on: res.data.online_reschedule_enabled === true,  // ONLINE_RESCHEDULE_v1 (Standard aus)
          pay_optional: res.data.pay_online_optional === true || res.data.pay_online_optional === 1,  // BOOKING_PAY_ONLINE_v1
        });
        setLoyaltyOn(!!res.data.loyalty_enabled);
        // LOYALTY_ENGINE_v1
        setLoyaltyRules({
          per_euro: Number(res.data.loyalty_points_per_euro != null ? res.data.loyalty_points_per_euro : 1),
          per_visit: Number(res.data.loyalty_points_per_visit != null ? res.data.loyalty_points_per_visit : 10),
        });
        // SALON_PROFILE_SAVE_v1: Profil-Card mit echten DB-Werten initialisieren
        setSalonProfile({
          name: res.data.name || "",
          address: res.data.address || "",
          phone: res.data.phone || "",
          email: res.data.email || "",
          opening_time: (res.data.opening_time || "08:00").slice(0,5),
          closing_time: (res.data.closing_time || "20:00").slice(0,5),
          buffer_minutes: typeof res.data.buffer_minutes === "number" ? res.data.buffer_minutes : 0,
          logo_url: res.data.logo_url || "",  // SALON_LOGO_v1
          booking_header_url: res.data.booking_header_url || "",  // BOOKING_V2_KOPF_v1
        });
      }
    }).catch(() => {});
  }, [_salonId]);
  // SALON_PROFILE_SAVE_v1: speichern (PUT /salons/<id>)
  const saveSalonProfile = async () => {
    if (_salonId == null) return;
    setSalonProfileSaving(true);
    setSalonProfileMsg("");
    try {
      const payload = {
        // NULLSAFE_AUDIT_v1 -- salonProfile.X kann null sein wenn DB-Feld leer → .trim() crasht
        name: String(salonProfile.name || "").trim(),
        address: String(salonProfile.address || "").trim(),
        phone: String(salonProfile.phone || "").trim(),
        email: String(salonProfile.email || "").trim(),
        opening_time: salonProfile.opening_time,
        closing_time: salonProfile.closing_time,
        buffer_minutes: Math.max(0, Math.min(60, Number(salonProfile.buffer_minutes) || 0)),
      };
      const res = await api.request("/salons/" + _salonId, {
        method: "PUT",
        body: JSON.stringify(payload),
      });
      if (res && res.ok) {
        setSalonProfileMsg("Gespeichert ✓");
        setBufferMin(payload.buffer_minutes); // BUFFER_v1 in Sync halten
        setTimeout(() => setSalonProfileMsg(""), 2500);
      } else {
        setSalonProfileMsg("Fehler: " + ((res && res.data && res.data.error) || res.status || "unbekannt"));
      }
    } catch (e) {
      setSalonProfileMsg("Netzwerkfehler: " + (e.message || "unbekannt"));
    } finally {
      setSalonProfileSaving(false);
    }
  };
  // OWNER_SAVE_AUDIT_v1 -- No-Show-Regeln speichern (Backend-Felder existieren bereits)
  const saveNoShow = async () => {
    if (_salonId == null) return;
    setNoShowMsg("");
    // PREPAY_FIELDS_VISIBLE_v1 -- eingeschaltete Vorkasse braucht eine Schwelle ab 1 EUR
    if (noShow.prepay_on && !(Number(String(noShow.prepay_threshold).replace(",", ".")) >= 1)) {
      setNoShowMsg("Bitte „Vorkasse ab Leistungswert“ mit mindestens 1 € angeben");
      setTimeout(() => setNoShowMsg(""), 4000);
      return;
    }
    const res = await api.request("/salons/" + _salonId, {
      method: "PUT",
      body: JSON.stringify({
        // BOOKING_PREPAY_v1 -- Schwelle 0/leer = aus (Server speichert NULL)
        // PREPAY_FIELDS_VISIBLE_v1 -- Schwelle immer speichern, Schalter separat
        prepay_enabled: noShow.prepay_on ? 1 : 0,
        prepay_threshold_eur: Math.max(0, Number(String(noShow.prepay_threshold).replace(",", ".")) || 0),
        prepay_pct: Math.max(10, Math.min(100, Number(noShow.prepay_pct) || 100)),
        prepay_timeout_min: Math.max(5, Math.min(1440, Number(noShow.prepay_timeout) || 15)),
        prepay_provider: noShow.prepay_provider === "paypal" ? "paypal" : noShow.prepay_provider === "stripe" ? "stripe" : "sumup",  // PREPAY_PAYPAL_v1 / PREPAY_STRIPE_v1
        noshow_fee: Math.max(0, Number(noShow.fee) || 0),
        cancellation_hours: Math.max(0, Number(noShow.cancel_h) || 0),
        online_cancellation_enabled: !!noShow.online_cancel_on,  // ONLINE_CANCEL_v1
        online_reschedule_enabled: !!noShow.online_reschedule_on,  // ONLINE_RESCHEDULE_v1
        pay_online_optional: !!noShow.pay_optional,  // BOOKING_PAY_ONLINE_v1
      }),
    });
    setNoShowMsg(res && res.ok ? "Gespeichert ✓" : "Fehler beim Speichern");
    setTimeout(() => setNoShowMsg(""), 2500);
  };
  // OWNER_SAVE_AUDIT_v1 -- Loyalty an/aus speichern
  // LOYALTY_ENGINE_v1 -- jetzt inkl. Punkte-Regeln (Backend-Gutschrift beim Kassieren)
  const saveLoyalty = async () => {
    if (_salonId == null) return;
    setLoyaltyMsg("");
    const res = await api.request("/salons/" + _salonId, {
      method: "PUT",
      body: JSON.stringify({
        loyalty_enabled: loyaltyOn ? 1 : 0,
        loyalty_points_per_euro: Math.max(0, Math.min(100, Math.round(Number(loyaltyRules.per_euro) || 0))),
        loyalty_points_per_visit: Math.max(0, Math.min(1000, Math.round(Number(loyaltyRules.per_visit) || 0))),
      }),
    });
    setLoyaltyMsg(res && res.ok ? "Gespeichert ✓" : "Fehler beim Speichern");
    setTimeout(() => setLoyaltyMsg(""), 2500);
  };
  const saveBuffer = async () => {
    if (_salonId == null) return;
    setBufferSaving(true);
    try {
      const res = await api.request("/salons/" + _salonId, {
        method: "PUT",
        body: JSON.stringify({ buffer_minutes: Math.max(0, Math.min(60, Number(bufferMin) || 0)) }),
      });
      if (!(res && res.ok)) alert("Speichern fehlgeschlagen.");
    } finally {
      setBufferSaving(false);
    }
  };
  const saveSmsSig = async () => {
    if (_salonId == null) return;
    setSmsSigSaving(true);
    setSmsSigMsg("");
    try {
      const res = await api.request("/salons/" + _salonId, {
        method: "PUT",
        body: JSON.stringify({ sms_signature: smsSig.trim() || null }),
      });
      setSmsSigMsg(res && res.ok ? "Gespeichert ✓" : "Fehler beim Speichern");
    } finally {
      setSmsSigSaving(false);
    }
  };
  const saveEscalationPhone = async () => {
    if (_salonId == null) return;
    setEscalationSaving(true);
    setEscalationMsg("");
    try {
      const res = await api.request("/salons/" + _salonId, {
        method: "PUT",
        body: JSON.stringify({ escalation_phone: escalationPhone.trim() || null }),
      });
      setEscalationMsg(res && res.ok ? "Gespeichert ✓" : "Fehler beim Speichern");
    } finally {
      setEscalationSaving(false);
    }
  };

        // AI_LOCK_v1 -- KI-Buchungsassistent: managedByAdmin ist true wenn Owner (Provider/Model/Key sind dann Admin-only).
        const [aiSettings, setAiSettings] = useState({
            enabled: 0, provider: "anthropic", apiKeyMasked: "", hasApiKey: false,
            model: "claude-sonnet-4-20250514", systemPrompt: "", managedByAdmin: false
        });
        const _aiIsAdmin = _ctxUser && _ctxUser.role === "admin";
        const [aiApiKey, setAiApiKey] = useState("");
        const [aiSaving, setAiSaving] = useState(false);

  useEffect(() => {
    // Check Stripe Connect status on mount
    api.request("/stripe/connect/status").then(res => {
      if (res.ok) setStripeStatus(res.data);
    });
  }, []);

        useEffect(() => {
            api.request("/salon/ai-settings").then(res => {
                if (res.ok) {
                    const d = res.data;
                    setAiSettings({
                        enabled: d.enabled || 0,
                        provider: d.provider || "anthropic",
                        apiKeyMasked: d.api_key_masked || "",
                        hasApiKey: d.has_api_key || false,
                        model: d.model || "claude-sonnet-4-20250514",
                        systemPrompt: d.system_prompt || "",
                        managedByAdmin: !!d.ai_managed_by_admin   // AI_LOCK_v1
                    });
                }
            });
        }, []);

  const handleStripeConnect = async () => {
    setStripeLoading(true);
    const res = await api.request("/stripe/connect/onboard", { method: "POST" });
    setStripeLoading(false);
    if (res.ok && res.data.url) {
      window.open(res.data.url, "_blank");
    } else {
      alert(res.data?.error || "Stripe-Verbindung fehlgeschlagen. Bitte versuchen Sie es später.");
    }
  };


        const handleSaveAI = async () => {
            setAiSaving(true);
            // AI_LOCK_v1 -- Owner sendet NUR enabled + system_prompt. Admin schickt zusätzlich provider/model/api_key.
            const payload = {
                enabled: aiSettings.enabled,
                system_prompt: aiSettings.systemPrompt
            };
            if (_aiIsAdmin) {
                payload.provider = aiSettings.provider;
                payload.model = aiSettings.model;
                if (aiApiKey) payload.api_key = aiApiKey;
            }
            const res = await api.request("/salon/ai-settings", { method: "PUT", body: JSON.stringify(payload) });
            setAiSaving(false);
            if (res.ok) {
                alert("KI-Einstellungen gespeichert!");
                setAiApiKey("");
                const r = await api.request("/salon/ai-settings");
                if (r.ok) {
                    const d = r.data;
                    setAiSettings({
                        enabled: d.enabled || 0,
                        provider: d.provider || "anthropic",
                        apiKeyMasked: d.api_key_masked || "",
                        hasApiKey: d.has_api_key || false,
                        model: d.model || "claude-sonnet-4-20250514",
                        systemPrompt: d.system_prompt || "",
                        managedByAdmin: !!d.ai_managed_by_admin
                    });
                }
            } else {
                alert("Fehler: " + (res.data?.error || "Unbekannter Fehler"));
            }
        };

  return (
  <div>
    <Header title="Einstellungen" subtitle="Salon & System-Konfiguration" />
    {/* SETTINGS_MASONRY_v1 (Toby 09.09.) -- zwei unabhängige Spalten statt Zeilen-Raster: das Raster zog jede
        Zeile auf die Höhe der höchsten Karte (leere Riesenfenster). Jetzt ist jede Karte so hoch wie ihr Inhalt;
        Reihenfolge spaltenweise. CSS-Regel .settings-masonry in index.html (break-inside: avoid). */}
    <div className="settings-masonry" style={{ columnCount: window.innerWidth < 768 ? 1 : 2, columnGap: 16 }}>

      {/* OWNER_PIN_v1 -- Inhaber-PIN (Karte blendet sich fuer Nicht-Inhaber selbst aus: Server antwortet 403) */}
      <OwnerPinCard />

      {/* SEASONAL_DECOR_v1 -- Saisonale Deko im Kalender (Wiesn, später weitere Anlässe); SEASONAL_DECOR_BOOKING_v1: gilt auch für die Buchungsseite */}
      {_salonId && bufferMin !== null && (
        <Card>
          <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <span role="img" aria-label={(activeSeason(today) || {}).badge?.label || "Brezn"} style={{ fontSize: 18 }}>{(activeSeason(today) || {}).badge?.emoji || "🥨"}</span> Saisonale Deko (Kalender + Buchungsseite)
            </span>
          </h3>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "10px 12px", background: seasonalDecor ? "rgba(34,197,94,0.10)" : "var(--cardHover)", borderRadius: 10, border: `1px solid ${COLORS.border}` }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>Zu Jahreszeit und Anlässen dezent dekorieren</div>
              {/* SEASONAL_DECOR_HERBST_v1 -- „Aktuell“ kommt aus der Anlass-Liste (seasonal.jsx), nicht mehr fest Oktoberfest */}
              <div style={{ fontSize: 11, color: "var(--textLight)" }}>{(() => { const cur = activeSeason(today); if (!cur) return "Gerade kein Anlass. "; const [, m, d] = cur.to.split("-"); return "Aktuell: " + cur.name + " bis " + Number(d) + "." + Number(m) + ". "; })()}Anlässe: Oktoberfest (Lebkuchenherz, Wäscheklammer, 🍻), Herbst (Ahornblatt, ☕), Halloween 26.–31.10. (Kürbis, 👻). Kalender: Plakette neben „Kalender", eine Grafik, das Zeichen auf Urlaubs- und Feierabend-Einträgen. Buchungsseite: Plakette im Kopf, Grafik, Gruß nach dem Buchen. Nie über Terminen oder Knöpfen. Nach dem Zeitraum verschwindet alles von selbst.</div>
            </div>
            <div onClick={toggleSeasonalDecor} style={{ width: 40, height: 22, borderRadius: 11, background: seasonalDecor ? "#22c55e" : "#6B7280", position: "relative", transition: "background 0.2s", flexShrink: 0, cursor: "pointer" }}>
              <div style={{ width: 18, height: 18, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: seasonalDecor ? 20 : 2, transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }} />
            </div>
          </div>
          {seasonalMsg && <div style={{ marginTop: 8, fontSize: 12, color: seasonalMsg.startsWith("Gespeichert") ? "#22c55e" : "#ef4444", fontWeight: 600 }}>{seasonalMsg}</div>}
        </Card>
      )}

      {/* BOOKING_THEME_v1 (Toby 08.10.2026) -- Farbe der Online-Buchungsseite, passend zur eigenen Webseite */}
      {_salonId && bufferMin !== null && (
        <Card>
          <h3 style={{ margin: "0 0 4px", fontSize: 15, fontWeight: 700 }}>🎨 Farbe der Online-Buchungsseite</h3>
          <div style={{ fontSize: 12, color: "var(--textLight)", marginBottom: 12 }}>Passend zu deiner Webseite. Ändert nur die Farben, nicht den Ablauf.</div>
          <div data-booking-theme-picker="1" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 10 }}>
            {BOOKING_THEMES.map(t => {
              const sel = bookingTheme === t.k;
              return (
                <button key={t.k} type="button" onClick={() => chooseBookingTheme(t.k)} aria-pressed={sel}
                  style={{ textAlign: "left", padding: 10, borderRadius: 12, cursor: "pointer", background: "var(--card)",
                           border: sel ? `2px solid ${t.c[0]}` : "2px solid var(--border)", boxShadow: sel ? `0 0 0 3px ${t.c[0]}22` : "none" }}>
                  <div style={{ height: 34, borderRadius: 8, overflow: "hidden", display: "flex", marginBottom: 8, border: "1px solid rgba(0,0,0,0.06)" }}>
                    <div style={{ flex: 3, background: `linear-gradient(135deg, ${t.c[0]}, ${t.c[1]})` }} />
                    <div style={{ flex: 1, background: t.c[2] }} />
                    <div style={{ flex: 1, background: t.c[3] }} />
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>{sel ? "✓ " : ""}{t.name}</div>
                  <div style={{ fontSize: 11, color: "var(--textLight)" }}>{t.sub}</div>
                </button>
              );
            })}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            {themeMsg ? <div style={{ fontSize: 12, color: themeMsg.startsWith("Gespeichert") ? "#22c55e" : "#ef4444", fontWeight: 600 }}>{themeMsg}</div> : <span />}
            {salonSlug && <a href={"/b/" + salonSlug} target="_blank" rel="noopener" style={{ fontSize: 12, fontWeight: 600, color: "var(--primary, #7C3AED)" }}>Buchungsseite ansehen ↗</a>}
          </div>
        </Card>
      )}

      {/* BOOKING_V2_KOPF_v1 -- Foto oben auf der Buchungsseite (direkt unter der Farbe) */}
      {_salonId && bufferMin !== null && (
        <SalonHeaderUpload salonId={_salonId} salonProfile={salonProfile} setSalonProfile={setSalonProfile} salonSlug={salonSlug} />
      )}

      {/* BUFFER_v1 -- Termin-Einstellungen / Pufferzeit */}
      {_salonId && bufferMin !== null && (
        <Card>
          <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700 }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              <Clock size={18} color={COLORS.primary} /> Termin-Einstellungen
            </span>
          </h3>
          <p style={{ fontSize: 12, color: COLORS.textLight, margin: "0 0 12px" }}>
            Pufferzeit zwischen Terminen (Reinigung, Vorbereitung). Wird automatisch von Online-Buchung & KI-Tele berücksichtigt.
          </p>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <label style={{ fontSize: 13, fontWeight: 600 }}>Pufferzeit:</label>
            <input type="number" min="0" max="60" step="5" value={bufferMin}
              onChange={e => setBufferMin(Math.max(0, Math.min(60, Number(e.target.value) || 0)))}
              style={{ width: 80, padding: "8px 10px", border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 14, background: "var(--card)", color: COLORS.text }} />
            <span style={{ fontSize: 13, color: COLORS.textLight }}>Minuten</span>
            <Btn size="sm" onClick={saveBuffer} disabled={bufferSaving}>{bufferSaving ? "Speichert…" : "Speichern"}</Btn>
          </div>
          <p style={{ fontSize: 11, color: COLORS.textLight, margin: "8px 0 0" }}>
            Empfehlung: 5–10 Min. Bei 0 Min werden Termine direkt aneinandergereiht.
          </p>

          {/* SMS_SIGNATURE_v1 -- Salon-Signatur für SMS */}
          <div style={{ marginTop: 18, paddingTop: 14, borderTop: `1px solid ${COLORS.border}` }}>
            <label style={{ fontSize: 13, fontWeight: 700, display: "block", marginBottom: 4, color: COLORS.text }}>
              ✍️ SMS-Signatur (Salon-Unterschrift)
            </label>
            <p style={{ fontSize: 12, color: COLORS.textLight, margin: "0 0 8px", lineHeight: 1.5 }}>
              Wird automatisch ans Ende jeder SMS gehängt — Bestätigungen, Erinnerungen, Absagen.
              So weiß der Kunde sofort, von welchem Salon die SMS kommt.
              Beispiele: <em>„💜 Dein Salon-Schönheit-Team"</em> · <em>„✂️ Julia &amp; Team · 0170 1234567"</em> · <em>„Bis bald · Studio TwentyFour"</em>
              <br />
              Wenn leer: Standard-Signatur „💜 Dein Salon-Team".
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input type="text" placeholder="z.B. 💜 Dein Salon-Schönheit-Team" maxLength="120"
                value={smsSig} onChange={e => setSmsSig(e.target.value)}
                style={{ flex: 1, padding: "8px 10px", border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 14, background: "var(--card)", color: COLORS.text }} />
              <Btn size="sm" onClick={saveSmsSig} disabled={smsSigSaving}>
                {smsSigSaving ? "…" : "Speichern"}
              </Btn>
            </div>
            {smsSigMsg && <div style={{ fontSize: 11, marginTop: 6, color: smsSigMsg.includes("✓") ? COLORS.success : COLORS.danger }}>{smsSigMsg}</div>}
          </div>

          {/* SICK_DAY_NOTIFY_v1 -- Notfall-Nummer mit ausfuehrlicher Erklaerung */}
          <div style={{ marginTop: 18, paddingTop: 14, borderTop: `1px solid ${COLORS.border}` }}>
            <label style={{ fontSize: 13, fontWeight: 700, display: "block", marginBottom: 4, color: COLORS.text }}>
              📞 Notfall-Nummer (z.B. dein Handy als Inhaber)
            </label>
            <div style={{ fontSize: 12, color: COLORS.textLight, margin: "0 0 10px", lineHeight: 1.5 }}>
              <p style={{ margin: "0 0 6px" }}>
                <strong style={{ color: COLORS.text }}>Wozu:</strong> Wenn ein:e Mitarbeiter:in kurzfristig krank wird,
                schickt <Wm /> automatisch eine Absage-SMS an die betroffenen Kund:innen.
                In dieser SMS wird diese Nummer als Direkt-Anruf-Link mitgeschickt.
              </p>
              <p style={{ margin: "0 0 6px" }}>
                <strong style={{ color: COLORS.text }}>Warum nicht die normale Salon-Nummer:</strong> Damit der enttäuschte Kunde
                nicht erst bei der KI-Tele landet, sondern dich (oder den Empfang) <strong>direkt</strong> erreicht —
                persönlich, ohne Wartezeit, ohne Frust.
              </p>
              <p style={{ margin: 0 }}>
                <strong style={{ color: COLORS.text }}>Wenn leer:</strong> wird die normale Salon-Nummer aus deinen Salon-Daten verwendet
                (der Kunde landet dann bei der KI-Tele, falls aktiv).
              </p>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input type="tel" placeholder="+49 170 1234567" value={escalationPhone}
                onChange={e => setEscalationPhone(e.target.value)}
                style={{ flex: 1, padding: "8px 10px", border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 14, background: "var(--card)", color: COLORS.text }} />
              <Btn size="sm" onClick={saveEscalationPhone} disabled={escalationSaving}>
                {escalationSaving ? "…" : "Speichern"}
              </Btn>
            </div>
            {escalationMsg && <div style={{ fontSize: 11, marginTop: 6, color: escalationMsg.includes("✓") ? COLORS.success : COLORS.danger }}>{escalationMsg}</div>}
          </div>
        </Card>
      )}

      {/* STRIPE_CONNECT_CARD_OUT_v1 (Toby 09.09.: „stripe-karte raus") -- Karte „Zahlungen — Stripe Connect" entfernt:
          Überbleibsel ohne Funktion, Vorkasse läuft über SumUp/PayPal (No-Show-Regeln). State/Handler stripeStatus bleiben ungenutzt. */}

      {/* SALON_PROFILE_SAVE_v1 -- echte Form mit State + Save-Handler (vorher: alles defaultValue, Speichern-Btn ohne onClick) */}
      <Card>
        <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 700 }}>Salon-Profil</h3>
        {/* SALON_LOGO_v1 -- Logo-Upload-Block */}
        <SalonLogoUpload currentUser={currentUser} salonProfile={salonProfile} setSalonProfile={setSalonProfile} />
        <Input label="Salonname" value={salonProfile.name} onChange={e => updSalonField("name", e.target.value)} />
        <Input label="Adresse" value={salonProfile.address} onChange={e => updSalonField("address", e.target.value)} />
        <Input label="Telefon (Festnetz für SMS-Kontakt)" value={salonProfile.phone} onChange={e => updSalonField("phone", e.target.value)} />
        <Input label="E-Mail" value={salonProfile.email} onChange={e => updSalonField("email", e.target.value)} type="email" />
        <div style={{ display: "flex", gap: 12 }}>
          <Input label="Öffnung" value={salonProfile.opening_time} onChange={e => updSalonField("opening_time", e.target.value)} type="time"
            onKeyDown={e => timeArrowKey(e, salonProfile.opening_time, "opening_time")} />
          <Input label="Schluss" value={salonProfile.closing_time} onChange={e => updSalonField("closing_time", e.target.value)} type="time"
            onKeyDown={e => timeArrowKey(e, salonProfile.closing_time, "closing_time")} />
        </div>
        <Input label="Pufferzeit (Min.)" value={salonProfile.buffer_minutes} onChange={e => updSalonField("buffer_minutes", e.target.value)} type="number" />
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 8 }}>
          <Btn onClick={saveSalonProfile} disabled={salonProfileSaving}>
            <Check size={14} /> {salonProfileSaving ? "Speichere..." : "Speichern"}
          </Btn>
          {salonProfileMsg && (
            <span style={{ fontSize: 12, color: salonProfileMsg.startsWith("Gespeichert") ? COLORS.success : COLORS.danger }}>
              {salonProfileMsg}
            </span>
          )}
        </div>
      </Card>

      {/* OWNER_SAVE_AUDIT_v1 -- Retell-Demo-Karte nur fuer Admin (Owner verwaltet KI nicht; AI_LOCK). */}
      {_aiIsAdmin && (<Card>
        <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 700 }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <Bot size={18} color={COLORS.primary} /> Retell AI — Sprachmodul
          </span>
        </h3>
        <div style={{ padding: 12, background: `${COLORS.success}08`, borderRadius: 8, marginBottom: 14, display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: COLORS.success, boxShadow: `0 0 6px ${COLORS.success}80` }} />
          <span style={{ fontWeight: 600, color: COLORS.success }}>Retell AI verbunden</span>
          <span style={{ color: COLORS.textLight }}>| Latenz: ~600ms | 0,07 $/Min.</span>
        </div>
        <Input label="Retell API Key" defaultValue="sk_ret_••••••••••••••••" />
        <Input label="Agent ID" defaultValue="agent_salon_schoenheit_v2" />
        <Select label="KI-Stimme" options={[
          { value: "emma-german-v2", label: "Emma (Deutsch, weiblich) — empfohlen" },
          { value: "hans-german-v1", label: "Hans (Deutsch, männlich)" },
          { value: "sofia-multilingual-v2", label: "Sofia (Multilingual, weiblich)" },
          { value: "alex-multilingual-v1", label: "Alex (Multilingual, männlich)" },
        ]} />
        <Input label="Begrüßungstext" defaultValue="Willkommen bei Salon Schönheit. Mein Name ist Ava, Ihre digitale Assistentin." />
        <Input label="Max. Gesprächsdauer (Sek.)" defaultValue="300" type="number" />
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Aktive Sprachen (Retell Multilingual)</label>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {[
              { code: "de", label: "Deutsch" },
              { code: "en", label: "English" },
              { code: "tr", label: "Türkçe" },
              { code: "ru", label: "Русский" },
              { code: "ar", label: "العربية" },
              { code: "fr", label: "Français" },
              { code: "pl", label: "Polski" },
            ].map(lang => (
              <label key={lang.code} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, padding: "4px 10px", borderRadius: 20, background: ["de","en","tr"].includes(lang.code) ? `${COLORS.primary}12` : "#f3f4f6", border: `1px solid ${["de","en","tr"].includes(lang.code) ? COLORS.primary : COLORS.border}` }}>
                <input type="checkbox" defaultChecked={["de","en","tr"].includes(lang.code)} style={{ marginRight: 2 }} />
                {lang.label}
              </label>
            ))}
          </div>
        </div>
        <div style={{ marginBottom: 14, padding: 12, background: "var(--cardHover)", borderRadius: 8, fontSize: 12 }}>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Telefonie-Routing</div>
          <div style={{ lineHeight: 2 }}>
            <div><Phone size={12} style={{ verticalAlign: -2 }} /> <strong>Salon-Nr:</strong> +49 30 1234 567 → Rufumleitung aktiv</div>
            <div><Bot size={12} style={{ verticalAlign: -2 }} /> <strong>Retell-Nr:</strong> +49 30 9876 5432 → KI nimmt an</div>
            <div><PhoneForwarded size={12} style={{ verticalAlign: -2 }} /> <strong>Eskalation:</strong> +49 170 1234567 → Salon klingelt</div>
          </div>
        </div>
        <Input label="Webhook-URL" defaultValue="https://api.salon-schoenheit.de/retell/webhook" />
        <Select label="Eskalation auslösen nach" options={[
          { value: "3", label: "3 erfolglosen Versuchen" },
          { value: "5", label: "5 erfolglosen Versuchen" },
          { value: "manual", label: "Nur auf Kundenwunsch" },
          { value: "sentiment", label: "Bei negativem Sentiment (KI-erkannt)" },
        ]} />
        <div style={{ display: "flex", gap: 8 }}>
          <Btn><Check size={14} /> Speichern</Btn>
          <Btn variant="secondary"><RefreshCw size={14} /> Testanruf</Btn>
        </div>
      </Card>)}

      <Card>
        <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 700 }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <MessageSquare size={18} color={COLORS.accent} /> KI-Buchungsassistent
          </span>
        </h3>
        {/* AI_LOCK_v1 -- Owner sieht nur Toggle + System-Prompt + Hinweis. Provider/Model/Key sind Admin-only. */}
        {!_aiIsAdmin ? (
          <p style={{ fontSize: 12, color: COLORS.textLight, margin: "0 0 14px", lineHeight: 1.7 }}>
            Ein KI-Chat auf deiner Online-Buchungsseite hilft Kunden den richtigen Service zu finden. <strong>Die KI ist im <Wm />-Plan inklusive</strong> — du musst weder einen API-Key besorgen noch das Modell auswählen, das übernimmt <Wm /> für dich.
          </p>
        ) : (
          <p style={{ fontSize: 12, color: COLORS.textLight, margin: "0 0 14px", lineHeight: 1.7 }}>
            Admin-Ansicht: Provider, Modell und API-Key sind nur hier sichtbar. Salon-Owner sehen nur das Aktivieren-Toggle und den System-Prompt.
          </p>
        )}
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer" }}>
            <input type="checkbox" checked={!!aiSettings.enabled} onChange={e => setAiSettings({...aiSettings, enabled: e.target.checked ? 1 : 0})} style={{ accentColor: COLORS.accent }} /> KI-Buchungsassistent aktivieren
          </label>
        </div>
        {_aiIsAdmin && (
          <>
            <Select label="LLM-Anbieter" value={aiSettings.provider} onChange={e => setAiSettings({...aiSettings, provider: e.target.value})} options={[
              /* OPENAI_REMOVED_v1 (B9, 17.09.2026) -- Shearly spricht nur noch mit Anthropic */
              { value: "anthropic", label: "Anthropic (Claude)" },
            ]} />
            <Input label="API-Key" placeholder={aiSettings.hasApiKey ? aiSettings.apiKeyMasked || "Key hinterlegt" : "sk-ant-... oder sk-..."} type="password" value={aiApiKey} onChange={e => setAiApiKey(e.target.value)} />
            <Select label="Modell" value={aiSettings.model} onChange={e => setAiSettings({...aiSettings, model: e.target.value})} options={[
              { value: "claude-sonnet-4-20250514", label: "Claude Sonnet 4 (schnell & günstig)" },
              { value: "claude-haiku-4-5-20251001", label: "Claude Haiku 4.5 (am günstigsten)" },
            ]} />
          </>
        )}
        {!_aiIsAdmin && (
          <div style={{ padding: 10, background: "var(--cardHover)", border: `1px solid ${COLORS.border}`, borderRadius: 8, marginBottom: 14, fontSize: 11.5, color: "var(--textLight)", display: "flex", alignItems: "flex-start", gap: 8 }}>
            <Lock size={14} color={COLORS.accent} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>KI-Modell, Provider und API-Key werden zentral von <Wm /> verwaltet. Status: <strong style={{ color: aiSettings.hasApiKey ? "#10B981" : "#F59E0B" }}>{aiSettings.hasApiKey ? "Aktiv ✓" : <>Wird vom <Wm />-Team eingerichtet</>}</strong></span>
          </div>
        )}
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 6, color: COLORS.textLight }}>System-Prompt (optional, eigene Anweisungen für die KI)</label>
          <textarea value={aiSettings.systemPrompt}
            onChange={e => setAiSettings({...aiSettings, systemPrompt: e.target.value})}
            placeholder={isKosmetikSalon() ? "z.B. „Erkläre unsere Behandlungen freundlich, betone unseren Bio-Anspruch." : "z.B. „Erkläre Färbeoptionen freundlich, betone unseren Bio-Anspruch."}
            rows={3}
            style={{ width: "100%", padding: "8px 10px", border: `1px solid ${COLORS.border}`, borderRadius: 8, fontSize: 13, background: "var(--card)", color: COLORS.text, resize: "vertical", fontFamily: "inherit" }} />
        </div>
        <div style={{ padding: 12, background: "var(--cardHover)", borderRadius: 8, marginBottom: 14, fontSize: 12, lineHeight: 1.8, border: `1px solid ${COLORS.border}` }}>
          <div style={{ fontWeight: 700, marginBottom: 4, color: COLORS.accent }}>So funktioniert es:</div>
          <div>1. Kunde öffnet deine Buchungsseite</div>
          <div>2. KI fragt: "Was möchtest du machen lassen?"</div>
          <div>3. Kunde beschreibt frei: {isKosmetikSalon() ? '"Meine Haut braucht mal wieder etwas Gutes"' : '"Ich will blondere Haare"'}</div>
          <div>4. KI erkennt → {isKosmetikSalon() ? "passende Behandlung, fragt bei Unklarheit nach" : "Balayage, fragt nach Haarlänge"}</div>
          <div>5. KI schlägt passenden Service mit ca.-Preis vor</div>
          <div>6. Kunde bestätigt → normaler Buchungsflow</div>
        </div>
        <div style={{ padding: 10, background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.35)", borderRadius: 8, marginBottom: 14, fontSize: 11, color: "var(--text)", display: "flex", alignItems: "flex-start", gap: 8 }}>
          <AlertTriangle size={14} color="#F59E0B" style={{ flexShrink: 0, marginTop: 1 }} />
          <span>Die KI zeigt <strong style={{ color: "#F59E0B" }}>ca.-Preise</strong>. Kunden sehen den Hinweis: „Der endgültige Preis wird im Salon nach Beratung festgelegt." So gibt es keine falschen Erwartungen.</span>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Btn onClick={handleSaveAI} disabled={aiSaving}><Check size={14} /> {aiSaving ? "..." : "Speichern"}</Btn>
        </div>
      </Card>

      {/* OWNER_SAVE_AUDIT_v1 -- Kassensystem-Demo-Karte entfernt (war Mock ohne Speichern).
          Echte POS-Anbindung laeuft ueber die eigene Seite PosSystemPage. */}

      
          <Card>
            <h3 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 16px" }}>SMS-Benachrichtigungen</h3>
            {(() => {
              const [smsSettings, setSmsSettings] = useState(null);
              const [smsMsg, setSmsMsg] = useState("");
              useEffect(() => {
                api.request("/salon/sms-settings").then(res => {
                  if (res.ok) setSmsSettings(res.data);
                });
              }, []);
              const toggleSms = async (field) => {
                const turningOn = !smsSettings[field];
                /* SMS_TRIAL_LOCK_v1 -- im Trial: Einschalten blockiert, Hinweis auf den Kauf */
                if (turningOn && smsSettings.trial_locked) { setSmsMsg("Testphase: SMS-Versand erst nach dem Kauf eines Tarifs (Menü „Mein Abo“)."); setTimeout(() => setSmsMsg(""), 6000); return; }
                const updated = { ...smsSettings, [field]: turningOn };
                setSmsSettings(updated);
                setSmsMsg("");
                const res = await api.request("/salon/sms-settings", { method: "PUT", body: JSON.stringify({ [field]: updated[field] }) });
                if (res.ok) setSmsMsg("Gespeichert");
                else if (res.status === 403 && res.data && res.data.code === "TRIAL_SMS_LOCKED") { setSmsSettings({ ...smsSettings, trial_locked: true }); setSmsMsg("Testphase: SMS-Versand erst nach dem Kauf eines Tarifs (Menü „Mein Abo“)."); setTimeout(() => setSmsMsg(""), 6000); return; }
                else setSmsMsg("Fehler beim Speichern");
                setTimeout(() => setSmsMsg(""), 2000);
              };
              const smsTypes = [
                // SMS_OWNER_TRIM_v1 -- Inhaber sieht nur noch Bestätigung + 24h-Erinnerung.
                // Manuelle Buchung, 2h-Erinnerung, Google-Bewertung steuert nur der Admin (Toby) pro Salon.
                { key: "sms_booking_confirm", label: "Buchungsbestätigung", desc: "SMS direkt nach einer Online-Buchung" },
                { key: "sms_reminder_24h", label: "24h-Erinnerung", desc: "Erinnerung am Vortag des Termins" },
              ];
              if (!smsSettings) return <div style={{ textAlign: "center", padding: 20, color: "#6b7280", fontSize: 13 }}>Laden...</div>;
              return (
                <div>
                  <p style={{ fontSize: 12, color: "#6b7280", margin: "0 0 14px", lineHeight: 1.7 }}>
                    {`Wählen Sie aus, welche SMS automatisch an Ihre Kunden gesendet werden.`}
                  </p>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {smsTypes.map(t => (
                      <div key={t.key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 12px", background: smsSettings[t.key] ? "rgba(34,197,94,0.10)" : "var(--cardHover)", borderRadius: 8, border: "1px solid " + (smsSettings[t.key] ? "rgba(34,197,94,0.35)" : "var(--border)"), cursor: "pointer" }} onClick={() => toggleSms(t.key)}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>{t.label}</div>
                          <div style={{ fontSize: 11, color: "var(--textLight)" }}>{t.desc}</div>
                        </div>
                        <div style={{ width: 40, height: 22, borderRadius: 11, background: smsSettings[t.key] ? "#22c55e" : "#6B7280", position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
                          <div style={{ width: 18, height: 18, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: smsSettings[t.key] ? 20 : 2, transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }} />
                        </div>
                      </div>
                    ))}
                  </div>
                  {smsMsg && <div style={{ marginTop: 10, fontSize: 12, color: smsMsg === "Gespeichert" ? "#22c55e" : "#ef4444", fontWeight: 600 }}>{smsMsg}</div>}
                </div>
              );
            })()}
          </Card>

<Card>
                  <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 700 }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                      <Globe size={18} color={COLORS.success} /> Google Business
                    </span>
                  </h3>
                  {(() => {
                    const [gs, setGs] = useState(null);
                    const [gMsg, setGMsg] = useState(""); const [showApiKey, setShowApiKey] = useState(false);
                    useEffect(() => {
                      api.request("/salon/google-settings").then(res => {
                        if (res.ok) setGs(res.data);
                      });
                    }, []);
                    const saveGoogle = async () => {
                      setGMsg("");
                      const res = await api.request("/salon/google-settings", { method: "PUT", body: JSON.stringify(gs) });
                      setGMsg(res.ok ? "Gespeichert" : "Fehler");
                      setTimeout(() => setGMsg(""), 2000);
                    };
                    if (!gs) return <div style={{ textAlign: "center", padding: 20, color: "#6b7280", fontSize: 13 }}>Laden...</div>;
                    return (
                      <div>
                        <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 12 }}>
                          Verbinden Sie Ihr Google Business Profil, um Bewertungen direkt in <Wm /> zu sehen.
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    <div style={{ position: "relative" }}>
                          <Input label="Google API Key" placeholder="AIzaSy..." type={showApiKey ? "text" : "password"} value={gs.google_api_key || ""} onChange={e => setGs({...gs, google_api_key: e.target.value})} />
                      <span onClick={() => setShowApiKey(!showApiKey)} style={{ position: "absolute", right: 10, top: 32, cursor: "pointer", fontSize: 12, color: COLORS.textLight, userSelect: "none" }}>{showApiKey ? "Verbergen" : "Anzeigen"}</span>
                    </div>
                          <Input label="Google Place ID" placeholder="ChIJ..." value={gs.google_place_id || ""} onChange={e => setGs({...gs, google_place_id: e.target.value})} />
                          <div style={{ fontSize: 11, color: COLORS.textLight, lineHeight: 1.8, marginTop: 4, background: COLORS.info+"08", padding: 10, borderRadius: 8 }}>
                            <strong>So gehts:</strong><br/>
                            1. Google Cloud Console &#8594; APIs &amp; Services &#8594; Credentials &#8594; API Key erstellen<br/>
                            2. Places API aktivieren<br/>
                            3. Place ID: Auf Google Maps Ihren Salon suchen &#8594; URL enthält die Place ID (ChIJ...)
                          </div>
                          <Btn onClick={saveGoogle}><Check size={14} /> Speichern</Btn>
                    <Btn variant="secondary" size="sm" style={{marginLeft: 8}} onClick={async () => {
                        setGMsg("Teste Verbindung...");
                        try {
                            const res = await api.request("/salon/google-reviews");
                            if (res.ok && res.data && res.data.name) {
                                setGMsg("\u2705 Verbunden: " + res.data.name + " (" + res.data.rating + "\u2b50)");
                            } else {
                                setGMsg("\u274c " + (res.data?.error || "Verbindung fehlgeschlagen"));
                            }
                        } catch(e) { setGMsg("\u274c Verbindungsfehler"); }
                    }}>Verbindung testen</Btn>
                          {gMsg && <div style={{ marginTop: 6, fontSize: 12, color: gMsg === "Gespeichert" ? "#22c55e" : "#ef4444", fontWeight: 600 }}>{gMsg}</div>}
                        </div>
                      </div>
                    );
                  })()}
                </Card>

                <Card>
        <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 700 }}>No-Show-Regeln</h3>
        {/* BOOKING_PREPAY_v1 -- Online-Vorkasse bei der Online-Buchung (SumUp-Checkout des Salons) */}
        {(() => { const prepayReady = noShow.prepay_provider === "paypal" ? noShow.paypal_ready : noShow.prepay_provider === "stripe" ? noShow.stripe_ready : noShow.sumup_ready; return (
        <div style={{ marginBottom: 14, paddingBottom: 14, borderBottom: `1px solid ${COLORS.border}` }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: prepayReady ? "pointer" : "not-allowed", fontWeight: 600, opacity: prepayReady ? 1 : 0.6 }}>
            <input type="checkbox" checked={!!noShow.prepay_on} disabled={!prepayReady} onChange={e => setNoShow(p => ({ ...p, prepay_on: e.target.checked }))} /> 💳 Online-Vorkasse bei der Online-Buchung
          </label>
          <p style={{ fontSize: 12, color: COLORS.textLight, margin: "6px 0 0", lineHeight: 1.5 }}>
            Ab dem eingestellten Leistungswert zahlt der Kunde direkt bei der Online-Buchung. Das Geld landet auf deinem SumUp-, PayPal- oder Stripe-Konto, der Termin ist erst nach Zahlungseingang bestätigt. Bleibt die Zahlung aus, wird der Termin nach der Zahlungsfrist automatisch freigegeben.
          </p>
          {/* PREPAY_PAYPAL_v1 -- Anbieter-Wahl */}
          <div style={{ display: "flex", gap: 14, margin: "10px 0 0", fontSize: 13 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
              <input type="radio" name="prepay_provider" checked={noShow.prepay_provider !== "paypal" && noShow.prepay_provider !== "stripe"} onChange={() => setNoShow(p => ({ ...p, prepay_provider: "sumup" }))} /> SumUp <span style={{ fontSize: 11, color: COLORS.textLight }}>(2,5 % je Zahlung)</span>
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
              <input type="radio" name="prepay_provider" checked={noShow.prepay_provider === "paypal"} onChange={() => setNoShow(p => ({ ...p, prepay_provider: "paypal" }))} /> PayPal <span style={{ fontSize: 11, color: COLORS.textLight }}>(ca. 2,99 % + 0,39 €)</span>
            </label>
            {/* PREPAY_STRIPE_v1 -- eigenes Stripe-Konto des Salons */}
            <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
              <input type="radio" name="prepay_provider" checked={noShow.prepay_provider === "stripe"} onChange={() => setNoShow(p => ({ ...p, prepay_provider: "stripe" }))} /> Stripe <span style={{ fontSize: 11, color: COLORS.textLight }}>(ca. 1,5 % + 0,25 € je Kartenzahlung)</span>
            </label>
          </div>
          {noShow.prepay_provider === "stripe" && (
            <div style={{ marginTop: 10, padding: "10px 12px", background: "var(--cardHover)", borderRadius: 8 }}>
              <p style={{ fontSize: 12, color: COLORS.textLight, margin: "0 0 8px", lineHeight: 1.5 }}>
                Eigenes Stripe-Konto nötig. Schlüssel: <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener" style={{ color: COLORS.primary }}>dashboard.stripe.com</a> → <em>Entwickler → API-Schlüssel</em> → geheimen Schlüssel (<code>sk_live_…</code>) oder einen eingeschränkten Schlüssel (<code>rk_live_…</code>, Rechte: Checkout Sessions schreiben, PaymentIntents lesen, Refunds schreiben) hier eintragen, dann „Verbindung testen“. Das Geld geht direkt auf dein Stripe-Konto.
              </p>
              <Input label="Stripe API-Schlüssel" type="password" value={noShow.stripe_prepay_secret_key} placeholder={noShow.stripe_prepay_has_key ? "•••••••••• (gespeichert, leer = behalten)" : "sk_live_… oder rk_live_…"} onChange={e => setNoShow(p => ({ ...p, stripe_prepay_secret_key: e.target.value, stripe_ready: false }))} />
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <Btn variant="secondary" onClick={testStripe} disabled={stripeTesting}>{stripeTesting ? "Prüfe…" : "Verbindung testen"}</Btn>
                {stripeTestMsg && <span style={{ fontSize: 12, fontWeight: 600, color: stripeTestMsg.includes("✓") ? COLORS.success : COLORS.danger }}>{stripeTestMsg}</span>}
                {!stripeTestMsg && noShow.stripe_ready && <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.success }}>Stripe verbunden ✓{noShow.stripe_livemode === false ? " (Testmodus-Schlüssel)" : ""}</span>}
                {!stripeTestMsg && !noShow.stripe_ready && <span style={{ fontSize: 12, color: COLORS.warning }}>⚠ Noch nicht verbunden – Schlüssel eintragen und testen.</span>}
              </div>
            </div>
          )}
          {noShow.prepay_provider !== "paypal" && noShow.prepay_provider !== "stripe" && !noShow.sumup_ready && (
            <p style={{ fontSize: 12, color: COLORS.warning, margin: "8px 0 0", lineHeight: 1.5 }}>
              ⚠ Dafür muss SumUp verbunden sein: unter <strong>Kassensystem</strong> SumUp wählen, API-Key + Merchant-Code eintragen und die Test-Verbindung erfolgreich ausführen.
            </p>
          )}
          {noShow.prepay_provider === "paypal" && (
            <div style={{ marginTop: 10, padding: "10px 12px", background: "var(--cardHover)", borderRadius: 8 }}>
              <p style={{ fontSize: 12, color: COLORS.textLight, margin: "0 0 8px", lineHeight: 1.5 }}>
                PayPal-Geschäftskonto nötig. Zugangsdaten: <a href="https://developer.paypal.com/dashboard/applications/live" target="_blank" rel="noopener" style={{ color: COLORS.primary }}>developer.paypal.com</a> → <em>Apps &amp; Credentials → Live → Create App</em> → Client-ID und Secret hier eintragen, dann „Verbindung testen“.
              </p>
              <Input label="PayPal Client-ID" value={noShow.paypal_client_id} onChange={e => setNoShow(p => ({ ...p, paypal_client_id: e.target.value, paypal_ready: false }))} />
              <Input label="PayPal Secret" type="password" value={noShow.paypal_client_secret} placeholder={noShow.paypal_has_secret ? "•••••••••• (gespeichert, leer = behalten)" : "Secret der Live-App"} onChange={e => setNoShow(p => ({ ...p, paypal_client_secret: e.target.value, paypal_ready: false }))} />
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, cursor: "pointer", marginBottom: 8 }}>
                <input type="checkbox" checked={!!noShow.paypal_sandbox} onChange={e => setNoShow(p => ({ ...p, paypal_sandbox: e.target.checked, paypal_ready: false }))} /> Sandbox (nur zum Testen, kein echtes Geld)
              </label>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <Btn variant="secondary" onClick={testPaypal} disabled={paypalTesting}>{paypalTesting ? "Prüfe…" : "Verbindung testen"}</Btn>
                {paypalTestMsg && <span style={{ fontSize: 12, fontWeight: 600, color: paypalTestMsg.includes("✓") ? COLORS.success : COLORS.danger }}>{paypalTestMsg}</span>}
                {!paypalTestMsg && noShow.paypal_ready && <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.success }}>PayPal verbunden ✓</span>}
                {!paypalTestMsg && !noShow.paypal_ready && <span style={{ fontSize: 12, color: COLORS.warning }}>⚠ Noch nicht verbunden – Zugangsdaten eintragen und testen.</span>}
              </div>
            </div>
          )}
          {/* BOOKING_PAY_ONLINE_v1 (Toby 08.10.2026) -- freiwillig online bezahlen, unabhaengig von der Pflicht-Vorkasse */}
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, marginTop: 14, cursor: prepayReady ? "pointer" : "not-allowed", fontWeight: 600, opacity: prepayReady ? 1 : 0.6 }}>
            <input type="checkbox" data-pay-optional="1" checked={!!noShow.pay_optional} disabled={!prepayReady} onChange={e => setNoShow(p => ({ ...p, pay_optional: e.target.checked }))} /> 💳 Online-Zahlung bei der Buchung anbieten (freiwillig)
          </label>
          <p style={{ fontSize: 12, color: COLORS.textLight, margin: "6px 0 0", lineHeight: 1.5 }}>
            Kunden können bei jeder Online-Buchung wählen: „Im Salon bezahlen“ oder „Jetzt online bezahlen“ – auch unter dem Vorkasse-Betrag. Der Termin steht sofort; wird nicht bezahlt, bleibt er bestehen und wird im Salon bezahlt. Im Kalender: 💳 = online bezahlt, ⏳ = Zahlung läuft. Es gilt die Zahlungsfrist unten.{prepayReady ? "" : " Dafür zuerst den Anbieter verbinden."}
          </p>
          {/* PREPAY_FIELDS_VISIBLE_v1 -- Schwelle/Anteil/Frist immer sichtbar + editierbar (Toby 14.09.), auch vor dem
              Verbinden des Anbieters; wirksam erst mit Schalter AN */}
          <div style={{ marginTop: 12 }}>
              {!(prepayReady && noShow.prepay_on) && (
                <p style={{ fontSize: 12, color: COLORS.textLight, margin: "0 0 8px", padding: "6px 10px", background: "var(--cardHover)", borderRadius: 8, lineHeight: 1.5 }}>
                  ℹ️ Gilt, sobald die Online-Vorkasse eingeschaltet ist{prepayReady ? "" : " (dafür zuerst den Anbieter verbinden)"}.
                </p>
              )}
              <Input label="Vorkasse ab Leistungswert (€)" type="number" value={noShow.prepay_threshold} onChange={e => setNoShow(p => ({ ...p, prepay_threshold: e.target.value }))} />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <Input label="Anteil online (%)" type="number" value={noShow.prepay_pct} onChange={e => setNoShow(p => ({ ...p, prepay_pct: e.target.value }))} />
                <Input label="Zahlungsfrist (Minuten)" type="number" value={noShow.prepay_timeout} onChange={e => setNoShow(p => ({ ...p, prepay_timeout: e.target.value }))} />
              </div>
              <p style={{ fontSize: 12, color: COLORS.textLight, margin: "-4px 0 0", lineHeight: 1.5 }}>
                {(() => {
                  // PREPAY_FIELDS_VISIBLE_v1 -- Beispiel rechnet mit den eingetragenen Werten
                  const _t = Number(String(noShow.prepay_threshold).replace(",", ".")) || 0;
                  const _p = Math.max(10, Math.min(100, Number(noShow.prepay_pct) || 100));
                  const _ex = Math.max(150, Math.ceil(_t / 10) * 10);
                  const _eur = v => v.toLocaleString("de-DE", { minimumFractionDigits: v % 1 ? 2 : 0, maximumFractionDigits: 2 }) + " €";
                  return _t >= 1
                    ? <>Beispiel: Ab {_eur(_t)} Leistungswert zahlt der Kunde {_p} % online{_p >= 100 ? " (Vollzahlung)" : ""}. Bei einer Leistung für {_eur(_ex)} sind das {_eur(Math.round(_ex * _p) / 100)}{_p < 100 ? `, den Rest (${_eur(_ex - Math.round(_ex * _p) / 100)}) zahlt er im Salon` : ""}. </>
                    : <>Trag ein, ab welchem Leistungswert die Vorkasse fällig ist und wie viel Prozent der Kunde online zahlt. </>;
                })()}
                Erstattung bei Absage: bis {Number(noShow.cancel_h) || 0} Std. vorher voll, danach darfst du die No-Show-Gebühr einbehalten (Knopf „Vorkasse erstatten" im Termin).
              </p>
            </div>
        </div>
        ); })()}
        <Input label="No-Show-Gebühr (€)" type="number" value={noShow.fee} onChange={e => setNoShow(p => ({ ...p, fee: e.target.value }))} />
        {/* ONLINE_CANCEL_v1 -- Kunden-Selbststornierung */}
        <div style={{ marginTop: 16, paddingTop: 14, borderTop: `1px solid ${COLORS.border}` }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer", fontWeight: 600 }}>
            <input type="checkbox" checked={!!noShow.online_cancel_on} onChange={e => setNoShow(p => ({ ...p, online_cancel_on: e.target.checked }))} /> Online-Stornierung durch Kunden erlauben
          </label>
          <p style={{ fontSize: 12, color: COLORS.textLight, margin: "6px 0 0", lineHeight: 1.5 }}>
            {/* CANCEL_TEXT_MAIL_v1 (Toby 20.10.2026) -- Link steht seit SMS_LINK_FREE_v1 in der Mail; die SMS nennt die Salon-Nummer */}
            Kunden bekommen in der Bestätigungs- und Erinnerungs-Mail einen persönlichen Link, über den sie ihren Termin selbst absagen können — bis zur eingestellten Frist. In der SMS steht stattdessen deine Telefonnummer (Links in SMS sortieren die Mobilfunknetze oft als Spam aus). Nach der Frist nur noch telefonisch (mögliche Ausfallgebühr).
          </p>
          {/* ONLINE_RESCHEDULE_v1 -- Kunden-Selbstverschiebung (derselbe Link, dieselbe Frist) */}
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer", fontWeight: 600, marginTop: 14 }}>
            <input type="checkbox" checked={!!noShow.online_reschedule_on} onChange={e => setNoShow(p => ({ ...p, online_reschedule_on: e.target.checked }))} /> Online-Verschieben durch Kunden erlauben
          </label>
          <p style={{ fontSize: 12, color: COLORS.textLight, margin: "6px 0 0", lineHeight: 1.5 }}>
            Über den Link in der Bestätigungs- und Erinnerungs-Mail kann der Kunde seinen Termin einmal selbst verschieben: gleiche Mitarbeiterin, gleiche Leistungen, nur Zeiten, die auch online frei sind, bis zur Frist unten. Du bekommst eine Meldung und eine E-Mail.
          </p>
          {(noShow.online_cancel_on || noShow.online_reschedule_on) && (
            <div style={{ marginTop: 12 }}>
              <Input label="Kostenlose Stornierung bis … Stunden vor dem Termin" type="number" value={noShow.cancel_h} onChange={e => setNoShow(p => ({ ...p, cancel_h: e.target.value }))} />
              <p style={{ fontSize: 12, color: COLORS.textLight, margin: "-4px 0 0", lineHeight: 1.5 }}>
                z.B. 12 = der Kunde kann bis 12 Stunden vor seinem Termin kostenlos online absagen. 0 = bis zum Terminbeginn.
              </p>
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Btn onClick={saveNoShow}><Check size={14} /> Speichern</Btn>
          {noShowMsg && <span style={{ fontSize: 12, fontWeight: 600, color: noShowMsg.startsWith("Gespeichert") ? COLORS.success : COLORS.danger }}>{noShowMsg}</span>}
        </div>
      </Card>

      <Card>
        <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 700 }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <Globe size={18} color={COLORS.info} /> Online-Kalender & Zugriffsrechte
          </span>
        </h3>
        <div style={{ fontSize: 13, lineHeight: 2.2, marginBottom: 14 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Inhaber & Mitarbeiter können den Kalender jederzeit online einsehen:</div>
          <div style={{ padding: 12, background: `${COLORS.info}08`, borderRadius: 8, marginBottom: 10 }}>
            <div style={{ fontFamily: "monospace", fontSize: 12, marginBottom: 4 }}>https://shearly.pro</div>{/* SETTINGS_REAL_v1: vorher app.shearly.de/kalender (falsch) */}
            <div style={{ fontSize: 11, color: COLORS.textLight }}>Zugriff per Browser (iOS Safari, Chrome, Windows) — kein App-Download nötig</div>
          </div>
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Zugriffsrechte pro Rolle:</label>
          {/* SETTINGS_REAL_v1 (Toby 14.10.2026, DEMO_REST) -- Rollen wie im System (vorher u. a. „Rezeption“, die es nicht gibt) */}
          {[
            { role: "Inhaber", rights: "Voller Zugriff: Kalender, Kunden, Finanzen, Einstellungen, Mitarbeiter", color: COLORS.primary },
            { role: "Mitarbeiter", rights: "Eigener Login. Welche Bereiche jemand sieht, stellst du je Person ein: Mitarbeiter → Team-Übersicht → Zugänge (oder „Salonleitung“).", color: COLORS.info },
            { role: "Salon-Login", rights: "Gemeinsamer Login für das Gerät im Salon (Kalender, Termine) — ohne persönliche Zuordnung.", color: COLORS.success },
            { role: "Steuerberatung", rights: <>Nur lesend: Termin-Liste, Provisionen und mit <Wm /> Kasse die Vergütung laut Vertrag, Vorkasse, Export aus der Kasse; Lohn-Daten (Stunden, Urlaub, Krank) nur nach deiner Freigabe.</>, color: COLORS.warning }, /* PAY_TAX_VIEW_v1 / PAYROLL_TAX_VIEW_v1 (Rechts-Check L1 A12) */
          ].map((r, i) => (
            <div key={i} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "8px 10px", borderBottom: `1px solid ${COLORS.border}` }}>
              <Badge text={r.role} color={r.color} />
              <span style={{ fontSize: 12, color: COLORS.textLight }}>{r.rights}</span>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 12, color: COLORS.textLight, padding: 10, background: "var(--cardHover)", borderRadius: 8 }}>
          {/* KASSE_HEUTE_v1 -- Ausnahme Tagesstand der Shearly Kasse */}
          Umsatzzahlen im Dashboard sieht nur der Inhaber. Ausnahme bei der <Wm /> Kasse: Wer den Tagesstand (Bar/Karte) sieht, stellst du unter „Kasse heute im Dashboard“ ein. Die Zugänge der Mitarbeiter kannst du jederzeit ändern.
        </div>
      </Card>

                    {React.createElement(TeamAccountCard)}

                    {React.createElement(KasseDayTotalsCard)}

                    {React.createElement(DataExportCard)}

      <Card>
        <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 700 }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <Zap size={18} color={COLORS.accent} /> Plattform
          </span>
        </h3>
        <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 768 ? "1fr" : "1fr 1fr 1fr", gap: 10, marginBottom: 14 }}>
          {[
            { platform: "iOS (iPhone/iPad)", status: "Unterstützt", desc: "Progressive Web App — zum Homescreen hinzufügen" },
            { platform: "Windows / macOS", status: "Unterstützt", desc: "Browser-basiert, Chrome / Edge / Safari" },
            { platform: "Android", status: "Unterstützt", desc: "Progressive Web App — Chrome" },
          ].map((p, i) => (
            <div key={i} style={{ padding: 12, background: "var(--cardHover)", borderRadius: 8, fontSize: 12 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>{p.platform}</div>
              <Badge text={p.status} color={COLORS.success} />
              <div style={{ marginTop: 6, color: COLORS.textLight }}>{p.desc}</div>
            </div>
          ))}
        </div>
        {/* SETTINGS_REAL_v1 (Toby 14.10.2026, DEMO_REST) -- Liste „API-Anbindungen“ entfernt: zeigte für jeden Salon fest
            „Verbunden“ (Retell, Stripe, Google, DATEV, POS) und eine REST-API mit Doku unter api.shearly.de, die es nicht gibt. */}
      </Card>

      {/* Loyalty / Kundenbindung (optional) */}
      <Card>
        <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 700 }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <Star size={18} color={COLORS.accent} /> Loyalty-Programm (optional)
          </span>
        </h3>
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
            <input type="checkbox" checked={loyaltyOn} onChange={e => setLoyaltyOn(e.target.checked)} />
            <strong>Loyalty-Programm aktivieren</strong>
          </label>
          <p style={{ fontSize: 12, color: COLORS.textLight, margin: "6px 0 0 26px" }}>
            Wenn aktiviert, sammeln Kunden automatisch Punkte pro Besuch und können diese gegen Rabatte oder Services einlösen.
          </p>
        </div>
        {/* LOYALTY_ENGINE_v1 -- Punkte-Regeln mit echtem Backend: Gutschrift automatisch beim Kassieren */}
        <div style={{ padding: 14, background: "var(--cardHover)", borderRadius: 8, marginBottom: 14 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10 }}>Punkte-Regeln</div>
          <div style={{ display: "grid", gridTemplateColumns: window.innerWidth < 768 ? "1fr" : "1fr 1fr", gap: 12 }}>
            <div>
              <label style={{ fontSize: 11, color: COLORS.textLight, display: "block", marginBottom: 4 }}>Punkte pro Besuch</label>
              <input type="number" min="0" max="1000" value={loyaltyRules.per_visit}
                onChange={e => setLoyaltyRules(r => ({ ...r, per_visit: e.target.value }))}
                style={{ width: "100%", padding: "8px 10px", border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 13, boxSizing: "border-box" }} />
            </div>
            <div>
              <label style={{ fontSize: 11, color: COLORS.textLight, display: "block", marginBottom: 4 }}>Punkte pro € Umsatz</label>
              <input type="number" min="0" max="100" value={loyaltyRules.per_euro}
                onChange={e => setLoyaltyRules(r => ({ ...r, per_euro: e.target.value }))}
                style={{ width: "100%", padding: "8px 10px", border: `1px solid ${COLORS.border}`, borderRadius: 6, fontSize: 13, boxSizing: "border-box" }} />
            </div>
          </div>
          <p style={{ fontSize: 11, color: COLORS.textLight, margin: "10px 0 0", lineHeight: 1.6 }}>
            Punkte werden <strong>automatisch beim Kassieren</strong> gutgeschrieben (Besuch + Umsatz des Termins).
          </p>
        </div>
        {/* LOYALTY_TIER_REWARDS_v1 -- Bronze ab 250 (~3 Besuche), je Stufe einmalig Produktkauf-Rabatt */}
        <div style={{ padding: 14, background: "var(--cardHover)", borderRadius: 8, marginBottom: 14 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10 }}>Stufen-System & Produkt-Rabatte</div>
          {[
            { name: "Bronze", min: "250 (≈ 3 Besuche)", pct: 5, color: "#CD7F32" },
            { name: "Silber", min: "500", pct: 7, color: "#C0C0C0" },
            { name: "Gold", min: "1500", pct: 10, color: "#FFD700" },
            { name: "Platin", min: "3000", pct: 15, color: "#B0C4DE" },
          ].map((tier, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderBottom: i < 3 ? `1px solid ${COLORS.border}` : "none", fontSize: 12 }}>
              <div style={{ width: 12, height: 12, borderRadius: "50%", background: tier.color }} />
              <strong style={{ width: 60 }}>{tier.name}</strong>
              <span style={{ color: COLORS.textLight }}>ab {tier.min} Punkten</span>
              <span style={{ marginLeft: "auto", fontWeight: 600 }}>einmalig {tier.pct} % auf einen Produktkauf</span>
            </div>
          ))}
          <p style={{ fontSize: 11, color: COLORS.textLight, margin: "10px 0 0", lineHeight: 1.6 }}>
            Neue Kunden starten <strong>ohne Stufe</strong> — Bronze gibt es erst ab 250 gesammelten Punkten.
            Die Stufe berechnet sich aus allen jemals gesammelten Punkten. Jeder Stufen-Rabatt kann
            pro Kunde <strong>einmal</strong> eingelöst werden (im Kundenprofil, beim Produktkauf an der Kasse).
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <Btn onClick={saveLoyalty}><Check size={14} /> Loyalty-Einstellungen speichern</Btn>
          {loyaltyMsg && <span style={{ fontSize: 12, fontWeight: 600, color: loyaltyMsg.startsWith("Gespeichert") ? COLORS.success : COLORS.danger }}>{loyaltyMsg}</span>}
        </div>
      </Card>

      {/* TSE-Kassenpflicht -- SETTINGS_REAL_v1 (Toby 14.10.2026, DEMO_REST): vorher feste Angaben „Verbundenes
          Kassensystem: Tillhub“, „Cloud-TSE (Swissbit) Aktiv“, „Letzte Synchronisation: Heute, 14:35 Uhr“, Checkliste mit
          festen Häkchen und „Shearly ist kein Kassensystem“. Jetzt nur zutreffende Hinweise + Weg zur Kasse. */}
      <Card>
        <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700 }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <Shield size={18} color={COLORS.danger} /> Kasse & TSE-Pflicht
          </span>
        </h3>
        <div style={{ fontSize: 13, lineHeight: 1.7, color: COLORS.text }}>
          Seit 2020 braucht jede elektronische Kasse eine zertifizierte TSE (Kassensicherungsverordnung).
          <ul style={{ margin: "8px 0 0 18px", padding: 0, color: COLORS.textLight }}>
            <li>Die <strong style={{ color: COLORS.text }}><Wm /> Kasse</strong> hat die TSE eingebaut (Cloud-TSE von fiskaly). Belege, Kassenbuch, DSFinV-K- und DATEV-Export findest du unter <strong style={{ color: COLORS.text }}>Kasse</strong>.</li>
            <li>Nutzt du ein anderes Kassensystem, ist dessen Anbieter für TSE und Kassenexport zuständig.</li>
          </ul>
        </div>
      </Card>
    </div>
  </div>
  );
};